import { of as observableOf, fromEvent, combineLatest } from 'rxjs';
import { Component, OnInit, ViewChild, Input, ViewEncapsulation } from '@angular/core';
import { SectionComponent } from './section.component';
import { KomaComponent } from './koma.component';
import { KYMW1400KomaPageModel } from '../kymw1400-koma-page/kymw1400-koma-page.model';

import { StageComponent, CoreShapeComponent } from 'ng2-konva';
import { Group } from 'konva/lib/Group';
import { Layer } from 'konva/lib/Layer';
import { Line } from 'konva/lib/shapes/Line';
import { Rect } from 'konva/lib/shapes/Rect';
import { Stage } from 'konva/lib/Stage';
import { Transformer } from 'konva/lib/shapes/Transformer';
import { Text } from 'konva/lib/shapes/Text';
import { Image } from 'konva/lib/shapes/Image';

import { Menu } from '@grapecity/wijmo.input';

import { CalcUtil } from '../../../../commons/kymw00/calc-util.service';
import { PlessAlert } from '../../../../shared/pless-alert/pless-alert.component';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { KYMW1400Model } from '../kymw1400.model';
import { GoodsInfoModel } from '../vo/goods-info.model';
import { RegistGoodsModel } from '../vo/regist-goods.model';

import { DeletePublishVoiceService } from '../api-services/delete-publish-voice.service';
import { UpdateKomaService } from '../api-services/update-koma.service';
import { RegistKomaService } from '../api-services/regist-koma.service';
import { RegistKomaGoodsService } from '../api-services/regist-koma-goods.service';
import { RegistGoodsService } from '../api-services/regist-goods.service';
import { ReplaceKomaService } from '../api-services/replace-koma.service';
import { ShiftKomaService } from '../api-services/shift-koma.service';
import { PlessViewUtils } from '../../../../shared/utils/pless-view-utils.service';

@Component({
  selector: 'app-page',
  templateUrl: 'page.component.html',
  styleUrls: ['page.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class PageComponent implements OnInit {
  @Input() leftOrRight = String('');
  @Input() kymw1400KomaPageModel: KYMW1400KomaPageModel = null;

  @ViewChild('stage', { static: true }) _stage: StageComponent;
  /** ページを描写するレイヤー */
  @ViewChild('layerPage', { static: true }) _layerPage: CoreShapeComponent;
  /** セクションを描写するレイヤー */
  @ViewChild('layerSection', { static: true }) _layerSection: CoreShapeComponent;
  /** コマを描写するレイヤー */
  @ViewChild('layerKoma', { static: true }) _layerKoma: CoreShapeComponent;
  /** コマプロキシを描写するレイヤー */
  @ViewChild('layerKomaProxy', { static: true }) _layerKomaProxy: CoreShapeComponent;
  /** コマ枠を描写するレイヤー */
  @ViewChild('layerKomaFrame', { static: true }) _layerKomaFrame: CoreShapeComponent;

  /* 各種ゲッタ */
  private get stage(): Stage {
    return this._stage.getStage();
  }
  private get layerPage(): Layer {
    return this._layerPage.getStage() as Layer;
  }
  private get layerSection(): Layer {
    return this._layerSection.getStage() as Layer;
  }
  private get layerKoma(): Layer {
    return this._layerKoma.getStage() as Layer;
  }
  private get layerKomaProxy(): Layer {
    return this._layerKomaProxy.getStage() as Layer;
  }
  private get layerKomaFrame(): Layer {
    return this._layerKomaFrame.getStage() as Layer;
  }

  // コマ操作区分コマ編集選択時
  public readonly SELECTED_KOMA_MOVE: number = Number(0);
  // コマ操作区分コマ入替選択時
  public readonly SELECTED_KOMA_REPLACE: number = Number(1);
  // コマ操作区分コマ詰め選択時
  public readonly SELECTED_KOMA_SHIFT: number = Number(2);

  private readonly REPLACE_ICON: string = String('./assets/icons/KYMW14/replacecursor.png');
  private readonly SHIFT_ICON: string = String('./assets/icons/KYMW14/shiftcursor.png');

  // 色関連の指定
  // 移動時背景色
  public readonly PROXY_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--proxy-color');
  public readonly LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--line-color');
  public readonly SELECTED_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--selected-color');
  public readonly NO_SELECTED_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--no-selected-color');

  // 固定数の指定
  public readonly DEFAULT_FONT_SIZE: number = Number(8); // 文字の大きさ
  public readonly DEFAULT_MARK_SIZE: number = Number(32); // マークの大きさ

  public readonly MARK_SIZE_FLAG: number = Number(0);
  public readonly IMAGE_SHOW_NUM: number = Number(3);

  // 色の定数
  public readonly SUB_LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--sub-line-color');
  public readonly FILL_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--fill-color');

  // 分割エリア内のグリッド分割数
  public readonly GRID_DIV_NUM: number = Number(4);
  public readonly GRID_SIZE_MIN: number = Number(1 / this.GRID_DIV_NUM);

  // 特定色
  // 作業領域のセクション背景色
  public readonly SAVE_BG_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--save-bg-color');
  // 通常領域のセクション背景色
  public readonly NORMAL_BG_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--nomal-bg-color');
  // 通常領域のセクション背景色
  public readonly SELECTED_BG_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--selected-bg-color');
  public readonly SAVE_AREA_TYPE: number = Number(1);
  public readonly NORAML_AREA_TYPE: number = Number(0);
  // 背景色
  public readonly BACKGROUND_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--page-background-color');
  // 線色（赤）
  public readonly BORDER_COLOR_RED: string = getComputedStyle(document.documentElement).getPropertyValue('--border-color-red');
  // 線色（青）
  public readonly BORDER_COLOR_BLUE: string = getComputedStyle(document.documentElement).getPropertyValue('--border-color-blue');

  // 選択ページフラグ
  public selected = Boolean(false);

  // 選択コマID
  private selectedKomaId = String('');

  // コマ入替フラグ
  private komaReplaceFlg = Boolean(false);
  // コマ衝突フラグ
  private collisionFlg = Boolean(false);

  public stageWidth: number = null;
  public stageHeight: number = null;

  // ページ情報
  private pageData: any = null;
  // ページID
  public pageId = String('');
  // ページ番号
  public pageNum = String('');
  // 横のグリッド数
  private divX = Number(1);
  // 縦のグリッド数
  private divY = Number(1);
  // 幅
  public width = Number(0);
  // 高さ
  public height = Number(0);

  // セクションリスト
  public sectionList: Array<SectionComponent> = new Array<SectionComponent>();

  // 移動元コマ情報
  private oldKomaX = 0;
  private oldKomaY = 0;
  private oldKomaW = 0;
  private oldKomaH = 0;

  // コマ入替変数
  // 入替元コマ
  private komaFr: KomaComponent = new KomaComponent();
  // 入替先コマ
  private komaTo: KomaComponent = new KomaComponent();

  // 別ページ
  public otherPageComp: PageComponent = null;

  // コンテキストメニュー
  private contextMenu: Menu;

  // コンテキストメニュー非活性フラグ
  private disabledGoodsInfo = Boolean(false);
  private disabledImageDirection = Boolean(false);
  private disabledMarkDirection = Boolean(false);
  private disabledHistory = Boolean(false);
  private disabledGoodsMentenance = Boolean(false);
  private disabledNvalGoodsMentenance = Boolean(false);
  private disabledMemberVoiceSelect = Boolean(false);
  private disabledMemberVoiceDelete = Boolean(false);
  private disabledDeleteKoma = Boolean(false);
  private disabledOpenKYMW1422 = false;

  /**
   * Creates an instance of PageComponent.
   * @param {KomaEditModel} komaEditModel コマ割編集画面共通モデル
   * @param {PlessDialogServiceSub} dialogService ダイアログサービス
   * @param {AddKomaService} addKomaService コマデータ追加サービス
   * @param {DeleteKomaService} deleteKomaService コマデータ削除サービス
   * @param {UpdateKomaService} updateKomaService コマデータ更新サービス
   * @param {RegistNewGoodsKomaService} registNewGoodsKomaService コマデータ更新サービス
   * @memberof PageComponent
   */
  constructor(
    private dialogService: PlessDialogServiceSub,
    private deletePublishVoiceService: DeletePublishVoiceService,
    private updateKomaService: UpdateKomaService,
    private registKomaService: RegistKomaService,
    private registKomaGoodsService: RegistKomaGoodsService,
    private registGoodsService: RegistGoodsService,
    private replaceKomaService: ReplaceKomaService,
    private shiftKomaService: ShiftKomaService,
    private kymw1400Model: KYMW1400Model,
  ) { }

  configStage = observableOf({
    width: this.stageWidth,
    height: this.stageHeight,
  });

  configLayerPage = observableOf({
    name: 'layerPage',
  });

  configLayerSection = observableOf({
    name: 'layerSection',
  });

  configLayerKoma = observableOf({
    name: 'layerKoma',
  });

  configLayerKomaProxy = observableOf({
    name: 'layerKomaProxy',
  });

  configLayerKomaFrame = observableOf({
    name: 'layerKomaFrame',
  });

  // グリッド
  private pageGrid = {
    positionX: 0,
    positionY: 0,
    sizeW: 0,
    sizeH: 0,
    divX: 0,
    divY: 0,
  };

  /*------------------------------------------------------------------------------------------
  - get プロパティ
  -----------------------------------*/
  /**
   * 選択されているSectionを返却する
   *
   * @return Section
   */
  public get selectedSection(): SectionComponent {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].isSelectedKoma()) {
        return this.sectionList[i];
      }
    }
    return null;
  }

  /**
   * 選択されたコマを返却する
   *
   * @return Koma:コマ
   */
  public get selectedKoma(): KomaComponent {
    if (this.selectedSection != null) {
      return this.selectedSection.selectedKoma();
    } else {
      return null;
    }
  }

  /**
   * 初期化処理
   *
   * @memberof PageComponent
   */
  ngOnInit() {
    // 左右どちらのページか取得する
    if (this.leftOrRight === 'left') {
      this.otherPageComp = this.kymw1400Model.rightView.page;
    } else {
      this.otherPageComp = this.kymw1400Model.leftView.page;
    }
  }

  /**
   * 掲載候補商品リストからのドラッグ開始イベント
   *
   * @param {*} e
   * @returns
   * @memberof PageComponent
   */
  public onDragEnter(e: any): void {
    // 掲載候補商品リストから商品がドラッグされていない場合処理しない
    if (this.kymw1400Model.goodsItem == null || !this.selected) {
      return;
    }
    // デフォルトのイベントを無効化
    e.preventDefault();

    // コマの枠線を表示する
    this.drawKomaFrame(e);
  }

  /**
   * 掲載候補商品リストからのドラッグ移動中イベント
   *
   * @param {*} e
   * @returns
   * @memberof PageComponent
   */
  public onDragOver(e): void {
    // 掲載候補商品リストから商品がドラッグされていない場合処理しない
    if (this.kymw1400Model.goodsItem == null || !this.selected) {
      return;
    }

    // コマの枠線を表示する
    this.moveKomaFrame(e);
  }

  /**
   * 掲載候補商品リストからのドロップ時イベント
   *
   * @returns {void}
   * @memberof PageComponent
   */
  public onDrop(e): void {
    // 掲載候補商品リストから商品がドラッグされていない場合処理しない
    if (this.kymw1400Model.goodsItem == null || !this.selected) {
      return;
    }

    // コマ枠線が非表示の場合は処理しない
    const komaFrame = this.layerKomaFrame.getChildren()[0];
    if (this.collisionFlg && komaFrame.opacity() === 0) {
      // コマの枠線情報をクリア
      this.layerKomaFrame.clear();
      this.layerKomaFrame.removeChildren();
      return;
    }
    const section: SectionComponent = this.getSectionByPosition(e.offsetX, e.offsetY);

    // Drag&Dropコマ衝突判定
    if (!this.collisionFlg) {
      // コマ追加
      const komaFrameRect = this.layerKomaFrame.getChildren()[0];
      const komaPosition = this.getKomaProxyPosition(section, komaFrameRect.x(), komaFrameRect.y(),
        komaFrameRect.width(), komaFrameRect.height());
      const tempKoma: KomaComponent = new KomaComponent;
      tempKoma.positionX = komaPosition.left / section.divX;
      tempKoma.positionY = komaPosition.top / section.divY;
      tempKoma.sizeW = (komaPosition.right - komaPosition.left) / section.divX;
      tempKoma.sizeH = (komaPosition.bottom - komaPosition.top) / section.divY;
      tempKoma.page = section.page;
      tempKoma.section = section;

      // コマの枠線情報をクリア
      this.layerKomaFrame.clear();
      this.layerKomaFrame.removeChildren();

      const komaInfoVOList: Array<any> = new Array<any>();
      komaInfoVOList.push(this.kymw1400Model.getKomaInfoVO(section, tempKoma, this.kymw1400Model.goodsItem.Pcode,
        this.kymw1400Model.goodsItem.ReceiptSOrderCode, this.kymw1400Model.goodsItem.StartOrderCode,
        this.kymw1400Model.goodsItem.OrderNum6Digits, this.kymw1400Model.goodsItem.ItemCode,
        this.kymw1400Model.goodsItem.OrderGoodsFlg));
      const vo = this.kymw1400Model.getRegistKomaGoodsVO(komaInfoVOList);
      this.registKomaGoodsService.execute(this.kymw1400Model, vo, null, () => {
        this.dragDropHandlerSuccess2();
      });
    } else {
      // コマ内商品クロス設定

      // コマの枠線情報をクリア
      this.layerKomaFrame.clear();
      this.layerKomaFrame.removeChildren();

      // Drop位置がクロス設定対象コマ内か確認
      let komaIndex = this.getIndexKomaListforCrossSet(section.komaList, e.offsetX, e.offsetY);
      if (Number(komaIndex) === -1) {
        // コマ領域の衝突ありだがDrop位置はコマ領域外のため、Dropイベント終了
        return;
      }

      // KYMW14/registGoods発行処理
      const registGoodsVo: RegistGoodsModel = new RegistGoodsModel();
      const goodsInfoVOList: Array<any> = new Array<any>();
      const goodsInfoVO: GoodsInfoModel = new GoodsInfoModel();

      goodsInfoVO.pCode = this.kymw1400Model.goodsItem.Pcode;
      goodsInfoVO.orderGoodsFlg = this.kymw1400Model.goodsItem.OrderGoodsFlg;
      goodsInfoVO.itemCode = this.kymw1400Model.goodsItem.ItemCode;
      goodsInfoVO.orderNum6Digits = this.kymw1400Model.goodsItem.OrderNum6Digits;
      goodsInfoVO.receiptSOrderCode = this.kymw1400Model.goodsItem.ReceiptSOrderCode;
      goodsInfoVO.startOrderCode = this.kymw1400Model.goodsItem.StartOrderCode;
      goodsInfoVO.publishAmountLimitNum = CalcUtil.getPublishAmountLimitNum(
                                            section,
                                            section.komaList[Number(komaIndex)]);
      goodsInfoVOList.push(goodsInfoVO);

      registGoodsVo.orderCode = this.kymw1400Model.selectedOrderCode;
      registGoodsVo.mediumType = this.kymw1400Model.selectedMediumType;
      registGoodsVo.coopCode = this.kymw1400Model.selectedCoopCode;
      registGoodsVo.pageNum = Number(this.kymw1400Model.selectedPageNum);
      registGoodsVo.sectionID = Number(section.sectionId);
      registGoodsVo.komaID = Number(section.komaList[Number(komaIndex)].komaId);
      registGoodsVo.goodsInfoVOList = goodsInfoVOList;

      this.registGoodsService.execute(
        this.kymw1400Model,
        registGoodsVo,
        this.kymw1400Model.pcodeList,
        (pcodeList) => {
          // アクティブなコマに配置完了イベント
          this.kymw1400Model.kymw1400Component.setKYMW1404ParameterSuccess2(pcodeList);
        }
      );
    }
  }

  /**
   * 掲載候補商品リストのドラッグ移動中、ページの外に離れた時のイベント
   *
   * @param {*} e
   * @returns
   * @memberof PageComponent
   */
  public onDragLeave(e): void {
    // 掲載候補商品リストから商品がドラッグされていない場合処理しない
    if (this.kymw1400Model.goodsItem == null) {
      return;
    }

    // コマの枠線をクリアする
    this.layerKomaFrame.clear();
    this.layerKomaFrame.removeChildren();
  }

  /**
   * コマの枠線を表示する
   *
   * @param {*} evtArgs イベント引数
   * @memberof PageComponent
   */
  private drawKomaFrame(e: any): void {
    // 枠線を表示
    const komaFrameRect = new Rect({
      name: 'komaFrameRect',
      fill: this.NO_SELECTED_COLOR,
      stroke: this.LINE_COLOR,
      strokeWidth: 1,
      opacity: 0.6,
    });

    // 対象セクション取得
    const section: SectionComponent = this.getSectionByPosition(e.offsetX, e.offsetY);
    if (section !== null) {
      // コマの位置を算出
      const komaPos = this.getKomaProxyPosition(section, e.offsetX - (section.drawSectionSizeW / section.divX / 2),
        e.offsetY - (section.drawSectionSizeH / section.divY / 2),
        section.drawSectionSizeW / section.divX, section.drawSectionSizeH / section.divY);
      const x = section.drawSectionPositionX + (komaPos.left * section.drawSectionSizeW) / section.divX;
      const y = section.drawSectionPositionY + (komaPos.top * section.drawSectionSizeH) / section.divY;

      // 枠線の位置を設定
      komaFrameRect.x(x);
      komaFrameRect.y(y);
      komaFrameRect.width(section.drawSectionSizeW / section.divX);
      komaFrameRect.height(section.drawSectionSizeH / section.divY);
    }

    // 枠線を描画
    this.layerKomaFrame.add(komaFrameRect);
    this.layerKomaFrame.draw();
  }

  /**
   * コマの枠線を移動させる
   *
   * @param {*} e イベント引数
   * @returns {void}
   * @memberof PageComponent
   */
  private moveKomaFrame(e: any): void {
    const komaFrameRect = this.layerKomaFrame.getChildren()[0];

    // 対象セクション取得
    const section: SectionComponent = this.getSectionByPosition(e.layerX, e.layerY);
    if (section == null) {
      komaFrameRect.opacity(0);
      this.layerKomaFrame.draw();
      return;
    }

    // 枠線の位置を算出
    // コマの位置を算出
    const komaPos = this.getKomaProxyPosition(section, e.offsetX - (section.drawSectionSizeW / section.divX / 2),
      e.offsetY - (section.drawSectionSizeH / section.divY / 2),
      section.drawSectionSizeW / section.divX, section.drawSectionSizeH / section.divY);
    const x = section.drawSectionPositionX + (komaPos.left * section.drawSectionSizeW) / section.divX;
    const y = section.drawSectionPositionY + (komaPos.top * section.drawSectionSizeH) / section.divY;

    // 他のコマと重なる場合
    this.collisionFlg = this.isCollisionKoma('', this, section, komaPos);

    // dropイベント有効化
    e.preventDefault();

    // 枠線の位置を設定
    komaFrameRect.x(x);
    komaFrameRect.y(y);
    komaFrameRect.width(section.drawSectionSizeW / section.divX);
    komaFrameRect.height(section.drawSectionSizeH / section.divY);

    // 枠線を描画
    this.layerKomaFrame.draw();
  }

  /**
   * サービスから取得した値をデータにマッピングする
   *
   * @param {*} pageData 取得データ
   * @returns {void}
   * @memberof PageComponent
   */
  public setData(pageData: any): void {
    this.pageData = pageData;

    // ページデータがない場合処理しない
    if (this.pageData == null) {
      return;
    }

    // ページ情報取得
    this.pageId = this.pageData.PageID;
    this.pageNum = this.pageData.PageNum;
    this.width = Number(this.pageData.SizeW);
    this.height = Number(this.pageData.SizeH);
    this.divX = Number(this.pageData.DivX);
    this.divY = Number(this.pageData.DivY);

    this.sectionList.length = 0;

    // セクションデータを設定
    if (this.pageData.SectionInfo != null) {
      this.pageData.SectionInfo.forEach(section => {
        const sectionInstance: SectionComponent = new SectionComponent(this.kymw1400Model);
        sectionInstance.page = this;
        sectionInstance.setData(section);
        this.sectionList.push(sectionInstance);
      });
    }

    // 描く
    this.reDraw();
  }

  /**
   * コマ情報追加
   *
   * @param {Array<any>} sectionInfoData
   * @memberof PageComponent
   */
  public addKomaData(sectionInfoData: Array<any>): void {
    const sectionInfoList: Array<any> = sectionInfoData;
    sectionInfoList.forEach(sectionInfo => {
      const section: SectionComponent = this.getSection(sectionInfo.SectionID);
      if (section != null) {
        section.addKomaData(sectionInfo.KomaInfo);
      }
    });
  }

  /**
   * Section取得
   *
   * @private
   * @param {string} sectionId
   * @returns {SectionComponent}
   * @memberof PageComponent
   */
  private getSection(sectionId: string): SectionComponent {
    this.sectionList.forEach(section => {
      if (section.sectionId === sectionId) {
        return section;
      }
    });
    return null;
  }

  /**
   * コマ取得
   *
   * @private
   * @param {string} komaId コマID
   * @returns {KomaComponent}
   * @memberof PageComponent
   */
  private getKoma(komaId: string): KomaComponent {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      for (let j = Number(0); j < this.sectionList[i].komaList.length; j++) {
        if (this.sectionList[i].komaList[j].komaId === komaId) {
          return this.sectionList[i].komaList[j];
        }
      }
    }
    return null;
  }

  /**
   * コマ情報更新
   *
   * @param {*} komaGroup
   * @returns {void}
   * @memberof PageComponent
   */
  public updateKomaData(komaGroup: any[]): void {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].updateKomaData(komaGroup)) {
        return;
      }
    }
  }

  /**
   * 線の再描画
   *
   * @memberof PageComponent
   */
  public reDraw(): void {
    // ページクリア
    this.layerPage.clear();
    this.layerPage.removeChildren();
    this.layerPage.draw();

    // ページデータからグリッド情報を設定
    this.pageGrid = {
      sizeW: Number(this.width * this.kymw1400KomaPageModel.sliderValue / 100),
      sizeH: Number(this.height * this.kymw1400KomaPageModel.sliderValue / 100),
      divX: Number(this.divX),
      divY: Number(this.divY),
      positionX: Number(this.pageData.PositionX),
      positionY: Number(this.pageData.PositionY),
    };

    // ページの表示倍率を変更する
    this.stage.width(this.pageGrid.sizeW);
    this.stage.height(this.pageGrid.sizeH);

    // グリッドの背景
    const pageRect: Rect = new Rect({
      name: 'pageRect',
      x: this.pageGrid.positionX,
      y: this.pageGrid.positionY,
      width: this.pageGrid.sizeW,
      height: this.pageGrid.sizeH,
      fill: this.FILL_COLOR,
      draggable: false,
      stroke: this.LINE_COLOR,
      strokeWidth: 3,
    });
    this.layerPage.add(pageRect);

    // 縦線
    let gridSpace: number = this.pageGrid.sizeW / this.pageGrid.divX / this.GRID_DIV_NUM;
    let gridCnt: number;
    for (gridCnt = 1; gridCnt < this.pageGrid.divX * this.GRID_DIV_NUM; gridCnt++) {
      let stWidth: number;
      let stroke: string;
      stWidth = (gridCnt % this.GRID_DIV_NUM === 0) ? 1 : 0.5;
      stroke = (gridCnt % this.GRID_DIV_NUM === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR;
      const koLine: Line = new Line({
        name: 'pageLine',
        points: [gridCnt * gridSpace, 0, gridCnt * gridSpace, this.pageGrid.sizeH],
        stroke: stroke,
        strokeWidth: stWidth,
      });
      this.layerPage.add(koLine);
    }

    // 横線
    gridSpace = this.pageGrid.sizeH / this.pageGrid.divY / this.GRID_DIV_NUM;
    for (gridCnt = 1; gridCnt < this.pageGrid.divY * this.GRID_DIV_NUM; gridCnt++) {
      const koLine: Line = new Line({
        name: 'pageLine',
        points: [0, gridCnt * gridSpace, this.pageGrid.sizeW, gridCnt * gridSpace],
        stroke: (gridCnt % this.GRID_DIV_NUM === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR,
        strokeWidth: (gridCnt % this.GRID_DIV_NUM === 0) ? 1 : 0.5,
      });
      this.layerPage.add(koLine);
    }
    this.layerPage.draw();

    // セクションクリア
    this.layerSection.clear();
    this.layerSection.removeChildren();
    this.layerSection.draw();

    // コマクリア
    this.layerKoma.clear();
    this.layerKoma.removeChildren();
    this.layerKoma.draw();

    // コマプロキシクリア
    this.layerKomaProxy.clear();
    this.layerKomaProxy.removeChildren();
    this.layerKomaProxy.draw();

    // セクション情報追加
    this.sectionList.forEach((section) => {
      this.showSectionGrid(section);
    });
  }

  /**
   * セクショングリッドを表示する
   *
   * @param {SectionComponent} section
   * @memberof PageComponent
   */
  showSectionGrid(section: SectionComponent): void {
    // 実数値に変換
    section.drawSectionPositionX = this.pageGrid.positionX + (section.positionX * this.pageGrid.sizeW);
    section.drawSectionPositionY = this.pageGrid.positionY + (section.positionY * this.pageGrid.sizeH);
    section.drawSectionSizeW = section.sizeW * this.pageGrid.sizeW;
    section.drawSectionSizeH = section.sizeH * this.pageGrid.sizeH;

    // グリッドの背景
    const sectionRect: Rect = new Rect({
      name: 'sectionRect',
      x: section.drawSectionPositionX,
      y: section.drawSectionPositionY,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
      fill: this.NORMAL_BG_COLOR,
      draggable: false,
      stroke: this.LINE_COLOR,
      strokeWidth: 3,
    });
    this.layerSection.add(sectionRect);

    // 縦線
    let gridSpace: number = section.drawSectionSizeW / section.divX / 2;
    let gridCnt: number;
    for (gridCnt = 1; gridCnt < section.divX * 2; gridCnt++) {
      let stWidth: number;
      let stroke: string;
      stWidth = (gridCnt % 2 === 0) ? 1 : 0.5;
      stroke = (gridCnt % 2 === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR;
      const sectionLineV: Line = new Line({
        name: 'sectionLine',
        points: [section.drawSectionPositionX + gridCnt * gridSpace, section.drawSectionPositionY,
        section.drawSectionPositionX + gridCnt * gridSpace, section.drawSectionPositionY + section.drawSectionSizeH],
        stroke: stroke,
        strokeWidth: stWidth,
      });
      this.layerSection.add(sectionLineV);
    }

    // 横線
    gridSpace = section.drawSectionSizeH / Number(section.divY) / 2;
    for (gridCnt = 1; gridCnt < Number(section.divY) * 2; gridCnt++) {
      const sectionLineH: Line = new Line({
        name: 'sectionLine',
        points: [section.drawSectionPositionX, section.drawSectionPositionY + gridCnt * gridSpace,
        section.drawSectionPositionX + section.drawSectionSizeW, section.drawSectionPositionY + gridCnt * gridSpace],
        stroke: (gridCnt % 2 === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR,
        strokeWidth: (gridCnt % 2 === 0) ? 1 : 0.5,
      });
      this.layerSection.add(sectionLineH);
    }
    this.layerSection.draw();

    // コマ情報追加
    section.komaList.forEach((koma) => {
      // 描画コマのサイズを設定
      koma.drawKomaPositionX = section.drawSectionPositionX
        + koma.positionX * section.drawSectionSizeW;
      koma.drawKomaPositionY = section.drawSectionPositionY
        + koma.positionY * section.drawSectionSizeH;
      koma.drawKomaSizeW = koma.sizeW * section.drawSectionSizeW;
      koma.drawKomaSizeH = koma.sizeH * section.drawSectionSizeH;

      this.drawKoma(koma);
    });
  }

  /**
   * コマを描画する
   *
   * @param {KomaComponent} koma コマデータ
   * @memberof PageComponent
   */
  drawKoma(koma: KomaComponent): void {
    // コマごとの画像データを取得する
    const Images = [];
    let imgObj: HTMLImageElement;
    let imageCount = 0;

    if (koma.goods.imageInfo != null) {
      imageCount = koma.goods.imageInfo.length;
      // 表示可能画像数を超えている場合は、表示可能画像数に合わせる
      if (imageCount > this.IMAGE_SHOW_NUM) {
        imageCount = this.IMAGE_SHOW_NUM;
      }

      // 画像URL設定
      for (let i = Number(0); i < imageCount; i++) {
        imgObj = <HTMLImageElement>document.createElement('img');
        if (koma.goods.imageInfo[i].ImagePath != null) {
          imgObj.src = koma.goods.imageInfo[i].ImagePath;
          imgObj.onerror = function (e: any) {
            e.target.src = './assets/img/error_image_clear.gif';
          };
          Images.push(imgObj);
        }
      }
    }

    // 空コマの場合、画像データがないのでそのまま描画
    if (Images.length === 0) {
      this.drawKomaImageLoaded(koma, Images);
    } else {
      // 画像データのロードが完了してからコマを描画する
      const imgLoads = [];
      for (let i = Number(0); i < Images.length; i++) {
        imgLoads.push(fromEvent(Images[i], 'load'));
      }
      combineLatest(
        imgLoads
      ).subscribe(
        value => {
          this.drawKomaImageLoaded(koma, Images);
        },
      );
    }

  }

  /**
   * コマを描画する
   *
   * @param {KomaComponent} koma コマデータ
   * @param {Array<HTMLImageElement>} Images 対象コマの画像データ
   * @memberof PageComponent
   */
  drawKomaImageLoaded(koma: KomaComponent, Images: Array<HTMLImageElement>): void {
    const targetKoma: Group = this.layerKoma.getChildren(function (node) {
      return node.id() === koma.komaId;
    })[0] as Group;
    if (targetKoma != null) {
      targetKoma.remove();
    }
    const targetKomaProxy: Group = this.layerKomaProxy.getChildren(function (node) {
      return node.id() === koma.komaId;
    })[0] as Group;
    if (targetKomaProxy != null) {
      targetKomaProxy.remove();
    }

    // コマのグループ
    const komaGroup: Group = new Group({
      id: koma.komaId,
      name: 'komaGroup',
      x: koma.drawKomaPositionX,
      y: koma.drawKomaPositionY,
      width: koma.drawKomaSizeW,
      height: koma.drawKomaSizeH,
      visible: this.kymw1400KomaPageModel.allKomaVisible,
    });

    // テキスト
    const komaText = this.goodsInfoString(koma);

    // 背景
    const komaRect: Rect = new Rect({
      name: 'komaRect',
      x: 0,
      y: 0,
      width: koma.drawKomaSizeW,
      height: koma.drawKomaSizeH,
      fill: koma.selected ? this.SELECTED_COLOR : this.NO_SELECTED_COLOR,
      stroke: this.LINE_COLOR,
      strokeWidth: 1,
    });
    komaGroup.add(komaRect);

    // 商品画像
    this.AddKomaImage(komaGroup, Images);

    // テキスト追加
    if (komaText != null) {
      for (let i = Number(0); i < komaText.length; i++) {
        komaGroup.add(komaText[i]);
      }
    }

    // コマを描画
    this.layerKoma.add(komaGroup);
    this.layerKoma.draw();

    /* コマプロキシの作成 */
    // プロキシをまとめるグループを作成
    const komaProxyGrp: Group = new Group({
      id: koma.komaId,
      name: 'komaProxyGrp',
      x: koma.drawKomaPositionX,
      y: koma.drawKomaPositionY,
      width: koma.drawKomaSizeW,
      height: koma.drawKomaSizeH,
      opacity: 0,
      draggable: !this.kymw1400KomaPageModel.pageLock &&
        this.kymw1400KomaPageModel.komaType === this.kymw1400KomaPageModel.KOMATYPE_MOVE,
      visible: this.kymw1400KomaPageModel.allKomaVisible,
    });

    // プロキシにイベントを追加
    this.addKomaEvent(komaProxyGrp, this);

    // プロキシの背景
    const komaProxyRect: Rect = new Rect({
      name: 'komaProxyRect',
      x: 0,
      y: 0,
      width: koma.drawKomaSizeW,
      height: koma.drawKomaSizeH,
      fill: this.NO_SELECTED_COLOR,
      stroke: this.LINE_COLOR,
      strokeWidth: 1,
      draggable: false,
    });
    komaProxyGrp.add(komaProxyRect);

    // プロキシにコンテキストメニューを設定する
    this.setContextMenu(komaProxyGrp, this);

    // プロキシのサイズ変更用オブジェクト
    const komaProxyTrans = new Transformer({
      id: koma.komaId,
      name: 'komaProxyTrans',
      rotateEnabled: false,
      resizeEnabled: !this.kymw1400KomaPageModel.pageLock &&
        this.kymw1400KomaPageModel.komaType === this.kymw1400KomaPageModel.KOMATYPE_MOVE,
      keepRatio: false,
      opacity: 0.6,
      visible: this.kymw1400KomaPageModel.allKomaVisible,
    });
    this.setContextMenu(komaProxyTrans, this);

    // プロキシのサイズ変更時に表示する矩形
    const komaProxyResizeRect = new Rect({
      id: koma.komaId,
      name: 'komaProxyResizeRect',
      x: koma.drawKomaPositionX,
      y: koma.drawKomaPositionY,
      width: koma.drawKomaSizeW,
      height: koma.drawKomaSizeH,
      fill: this.NO_SELECTED_COLOR,
      stroke: this.LINE_COLOR,
      strokeWidth: 1,
      opacity: 0,
      draggable: false,
    });
    this.layerKomaProxy.add(komaProxyResizeRect);

    // プロキシにコマサイズ変更時の枠を設定
    this.setResizeDragRange(komaProxyTrans, komaProxyGrp);

    // コマサイズ変更時のイベント追加
    this.addResizeEvent(komaProxyTrans, komaProxyGrp, komaProxyResizeRect, this);

    // プロキシを描画
    this.layerKomaProxy.add(komaProxyGrp);
    this.layerKomaProxy.add(komaProxyTrans);
    komaProxyTrans.setNodes([komaProxyGrp]);
    this.layerKomaProxy.draw();
  }

  /**
   * コマに表示する文字を設定する
   *
   * @param {KomaComponent} koma コマデータ
   * @returns {Array<Text>}
   * @memberof PageComponent
   */
  private goodsInfoString(koma: KomaComponent): Array<Text> {

    if (koma.goods.layoutInfo == null) {
      return null;
    }
    const goodsList = koma.goods.layoutInfo;
    let goodsText = String('');
    const pricePatternText: Array<any> = new Array<any>();
    const retKomaTextList: Array<any> = new Array<any>();
    const promotionNameText = koma.goods.promotionName;

    goodsList.forEach(goods => {
      // 商品情報設定
      goodsText +=
        this.getGoodsInfoString(this.appendOcrMark(goods.OrderNumSeqNo, goods.OcrPublishFlg)) +
        this.getGoodsInfoString(this.appendWPublishMark(goods.PCode, goods.MultiPublishString)) +
        this.getGoodsInfoString(goods.GuideGoodsName) +
        this.getGoodsInfoString(goods.GuideStandardName) +
        this.getGoodsInfoString(goods.MakerName) +
        this.getGoodsInfoString(goods.ThisTimeSupplyPrice) +
        this.getGoodsInfoString(goods.OrderNum);

      // 価格パターン
      pricePatternText.push(this.getPricePattern(goods.EdlpDiv, goods.PricePattern));

      // 企画メンテで更新したPコードの場合
      if (this.kymw1400Model.reloadPcode === goods.PCode) {
        // Pコードを初期化
        this.kymw1400Model.reloadPcode = 'xxxxxxxxxx';

        this.kymw1400Model.reloadPcodeList = this.createGoodsList(koma.goods.layoutInfo);

        // 実績履歴を再描画
        this.kymw1400Model.kymw1400Component.sendInputVOToAllPopupDialog(false, false, false, false, true, false, false);
      }
    });

    // プロモーション区分名
    retKomaTextList.push(new Text({
      name: 'komaPromotionName',
      x: koma.drawKomaSizeW - 17,
      y: 5,
      width: koma.drawKomaSizeW * 0.3,
      height: 5,
      fontSize: Number(10),
      text: promotionNameText,
      shadowColor: getComputedStyle(document.documentElement).getPropertyValue('--page-shadow-color'),
      shadowBlur: 0,
      shadowOffset: { x: -1.5, y: -1.5 },
      fontStyle: 'Bold',
      visible: this.kymw1400Model.komaTextViewFlg,
    }));

    // 商品情報
    retKomaTextList.push(new Text({
      name: 'komaGoodsInfo',
      x: 5,
      y: 5,
      width: koma.drawKomaSizeW * 0.9,
      height: koma.drawKomaSizeH * 0.9,
      fontSize: Number(this.kymw1400KomaPageModel.fontSize),
      text: goodsText,
      shadowColor: getComputedStyle(document.documentElement).getPropertyValue('--page-shadow-color'),
      shadowBlur: 0,
      shadowOffset: { x: -1.5, y: -1.5 },
      fontStyle: 'Bold',
      visible: this.kymw1400Model.komaTextViewFlg,
    }));

    // 価格パターン
    let pricePatternCount = Number(0);
    for (let i = Number(0); i < pricePatternText.length; i++) {
      if (pricePatternText[i] != null) {
        pricePatternCount++;
        retKomaTextList.push(new Text({
          name: 'komaPricePattern',
          x: koma.drawKomaSizeW - (15 * pricePatternCount),
          y: koma.drawKomaSizeH - 15,
          width: koma.drawKomaSizeW * 0.2,
          height: 15,
          fontSize: Number(14),
          text: pricePatternText[i].text,
          fill: pricePatternText[i].color,
          visible: this.kymw1400Model.komaImageViewFlg,
        }));
      }
    }
    // コマグループに文字を追加する
    return retKomaTextList;
  }

  private appendOcrMark(seqNo: string, ocrFlg: string) {
    return ocrFlg === '1' ? `${seqNo} ★` : seqNo;
  }

  private appendWPublishMark(pCode: string, multiPublishString: string) {
    return this.isWPublishedGoods(multiPublishString) ? `${pCode}（${multiPublishString}）` : pCode;
  }

  private isWPublishedGoods(multiPublishString: string) {
    return multiPublishString === 'Ｗ親' || multiPublishString === 'Ｗ子' || multiPublishString === 'Ｗ跨親' || multiPublishString === 'Ｗ跨子';
  }

  /**
   * コマに表示する画像を設定する
   *
   * @param {Group} komaGroup コマグループ
   * @param {Array<HTMLImageElement>} Images 対象コマの画像データ
   * @memberof PageComponent
   */
  AddKomaImage(komaGroup: Group, Images: Array<HTMLImageElement>): void {
    let i: number;
    let imageX: number;
    let imageY: number;
    let imageW: number;
    let imageH: number;

    // 画像の数によってサイズを調整
    for (i = 0; i < Images.length; i++) {
      switch (i) {
        case 0:
          imageX = komaGroup.width() * 0.05;
          imageY = komaGroup.height() * 0.05;
          if (Images.length === 1) {
            imageW = komaGroup.width() * 0.75;
            imageH = komaGroup.height() * 0.75;
          } else {
            imageW = komaGroup.width() * 0.65;
            imageH = komaGroup.height() * 0.65;
          }
          break;
        case 1:
          imageX = komaGroup.width() * 0.55;
          imageY = komaGroup.height() * 0.55;
          imageW = komaGroup.width() * 0.4;
          imageH = komaGroup.height() * 0.4;
          break;
        case 2:
          imageX = komaGroup.width() * 0.65;
          imageY = komaGroup.height() * 0.1;
          imageW = komaGroup.width() * 0.3;
          imageH = komaGroup.height() * 0.3;
          break;
      }

      // 縦・横の倍率を設定する
      const scalex: number = imageW / Images[i].width;
      const scaley: number = imageH / Images[i].height;
      const scaleBoth: number = (scalex < scaley) ? scalex : scaley;

      // コマグループに画像を追加する
      const koImg = new Image({
        name: 'komaImage',
        x: imageX,
        y: imageY,
        image: Images[i],
        scaleX: scaleBoth,
        scaleY: scaleBoth,
        visible: this.kymw1400Model.komaImageViewFlg,
      });
      komaGroup.add(koImg);
    }
  }

  /**
   * 商品情報表示HTML文字列を作成する
   *
   * @private
   * @param {string} source 商品情報文字列
   * @returns {string}
   * @memberof PageComponent
   */
  private getGoodsInfoString(source: string): string {
    if (PlessViewUtilsSub.isEmpty(source)) {
      return '';
    } else {
      return source + '\n';
    }
  }

  /**
   * 価格パタンによる文字編集
   *
   * @private
   * @param {string} edlpDiv
   * @param {string} pricePattern
   * @returns {*}
   * @memberof PageComponent
   */
  private getPricePattern(edlpDiv: string, pricePattern: string): any {
    if (this.kymw1400Model.selectedDutiesFormDiv !== '0' && this.kymw1400Model.selectedDutiesFormDiv !== '1') {
      return null;
    }

    if (edlpDiv === '0') {
      switch (pricePattern) {
        case '1':
          return { text: '■', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
        case '2':
          return { text: '■', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
        case '3':
          return { text: '■', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
      }
    } else {
      switch (pricePattern) {
        case '0':
          return { text: '●', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
        case '1':
          return { text: '●', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
        case '2':
          return { text: '●', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
        case '3':
          return { text: '●', color: getComputedStyle(document.documentElement).getPropertyValue('--price-pattern-colors-' + pricePattern) };
      }
    }
    return null;
  }

  /**
   * 商品のXMLリスト情報を生成する※実績履歴用
   */
  public createGoodsList(layoutInfo: any): Array<any> {
    const goodsList: Array<any> = new Array<any>();
    const selectedGoods: any = new Object();
    // レイアウト(商品)情報処理
    if (layoutInfo != null) {
      layoutInfo.forEach(goods => {
        selectedGoods.PCode = goods.PCode;
        selectedGoods.GuideGoodsName = goods.GuideGoodsName;
        selectedGoods.GuideStandardName = goods.GuideStandardName;
        goodsList.push(selectedGoods);
      });
    }

    return goodsList;
  }

  /**
   * コマサイズ変更のドラッグ範囲を設定
   *
   * @param {Transformer} transformer
   * @param {Group} proxyGroup
   * @memberof PageComponent
   */
  setResizeDragRange(transformer: Transformer, proxyGroup: Group): void {
    const rect: any[] = transformer.getChildren();
    const groupWidth = proxyGroup.width() * proxyGroup.scaleX();
    const groupHeight = proxyGroup.height() * proxyGroup.scaleY();
    // コマリサイズ用の掴める範囲を設定
    const width = groupWidth * 0.15 < 5 ? 5 : groupWidth * 0.15;
    const height = groupHeight * 0.15 < 5 ? 5 : groupHeight * 0.15;

    transformer.anchorStyleFunc((anchor) => {

      anchor.opacity(0);
      const name = anchor.name().split(' ')[0];
      switch (name) {
        case 'top-left':
          anchor.offsetX(0);
          anchor.offsetY(0);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'top-center':
          anchor.offsetX((groupWidth - width * 2) / 2);
          anchor.offsetY(0);
          anchor.width(groupWidth - width * 2);
          anchor.height(height);
          break;
        case 'top-right':
          anchor.offsetX(width);
          anchor.offsetY(0);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'middle-left':
          anchor.offsetX(0);
          anchor.offsetY((groupHeight - height * 2) / 2);
          anchor.width(width);
          anchor.height(groupHeight - height * 2);
          break;
        case 'middle-right':
          anchor.offsetX(width);
          anchor.offsetY((groupHeight - height * 2) / 2);
          anchor.width(width);
          anchor.height(groupHeight - height * 2);
          break;
        case 'bottom-left':
          anchor.offsetX(0);
          anchor.offsetY(height);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'bottom-center':
          anchor.offsetX((groupWidth - width * 2) / 2);
          anchor.offsetY(height);
          anchor.width(groupWidth - width * 2);
          anchor.height(height);
          break;
        case 'bottom-right':
          anchor.offsetX(width);
          anchor.offsetY(height);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'back':
          anchor.width(groupWidth);
          anchor.height(groupHeight);
          anchor.stroke(this.LINE_COLOR);
          anchor.fill(this.PROXY_COLOR);
          break;
      }
    });

    rect.forEach(item => {
      item.opacity(0);
      if ('back' === item.name()) {
        item.width(groupWidth);
        item.height(groupHeight);
        item.stroke(this.LINE_COLOR);
        item.fill(this.PROXY_COLOR);
      }
    });
  }

  /**
   * コマに対してイベントを追加する
   *
   * @param {Group} komaProxyGroup
   * @param {PageComponent} pageComp
   * @memberof PageComponent
   */
  addKomaEvent(komaProxyGroup: Group, pageComp: PageComponent): void {
    let dropEnableFlag = Boolean(false);

    // マウスダウン時イベント
    // コマの背景色を変更する
    komaProxyGroup.on('mousedown', function (e: any) {
      if (getFromPage().kymw1400KomaPageModel.komaType === getFromPage().kymw1400KomaPageModel.KOMATYPE_MOVE) {
        // /** コマ情報のバックアップ(選択時) **/
        pageComp.backupOldKoma(e.target.getParent());
      }

      // クリックされたコマと、現在選択中のコマが違えば処理実行
      if (pageComp.selectedKomaId === e.target.getParent().id()) {
        return;
      }

      const leftSelectedKomaId: string = pageComp.kymw1400Model.leftView.page.selectedKomaId;
      const rightSelectedKomaId: string = pageComp.kymw1400Model.rightView.page.selectedKomaId;

      if (!PlessViewUtilsSub.isEmpty(leftSelectedKomaId) || !PlessViewUtilsSub.isEmpty(rightSelectedKomaId)) {
        pageComp.kymw1400KomaPageModel.otherPageFlag
          = (getFromPage().leftOrRight !== pageComp.leftOrRight) ? true : false;
        // 入替元のコマグループ
        const komaGroupFrom: Group = getFromPage().layerKoma.getChildren(function (node) {
          return node.id() === getFromPage().selectedKomaId;
        })[0] as Group;
        const komaFrom: KomaComponent = getFromPage().getKoma(komaGroupFrom.id());
        const komaTo: KomaComponent = pageComp.getKoma(komaProxyGroup.id());

        if (getFromPage().kymw1400KomaPageModel.komaType === getFromPage().kymw1400KomaPageModel.KOMATYPE_REPLACECURSOR) {
          // 選択コマの背景色変更
          changeKomaBackColor(pageComp.layerKoma, pageComp.SELECTED_COLOR);
          // コマ入替可能チェック
          if (!pageComp.canReplaceKoma(komaFrom, komaTo, getFromPage(), pageComp, () => {
            reset();
          })) {
            return;
          }

          pageComp.dialogService.openAlertGetMessage('Q0152', ['コマ入替'], (closeDetail: any) => {
            // 「はい」を押下した場合、コマ入替処理
            if (closeDetail === PlessAlert.Yes) {
              pageComp.replaceKoma(komaFrom, komaTo);
            } else {
              reset();
            }
          });
          return;
        } else if (getFromPage().kymw1400KomaPageModel.komaType === getFromPage().kymw1400KomaPageModel.KOMATYPE_SHIFTCURSOR) {
          // 選択コマの背景色変更
          changeKomaBackColor(pageComp.layerKoma, pageComp.SELECTED_COLOR);
          // コマ詰め可能チェック
          if (!pageComp.canShiftKoma(komaTo, getFromPage(), pageComp, () => {
            reset();
          })) {
            return;
          }

          pageComp.dialogService.openAlertGetMessage('Q0152', ['コマ詰め'], (closeDetail: any) => {
            // 「はい」を押下した場合、コマ詰め処理
            if (closeDetail === PlessAlert.Yes) {
              pageComp.shiftKoma(komaFrom, komaTo);
            } else {
              reset();
            }
          });
          return;
        }
      }

      /** コマ情報のバックアップ(選択時) **/
      pageComp.backupOldKoma(e.target.getParent());

      // ページに選択コマ(自分)IDを通知して他のコマを非選択状態にする
      pageComp.setSelectedKoma(e.target.getParent().id());
      // 各レイヤーを取得
      const targetKoma: Group = pageComp.layerKoma.getChildren(function (node) {
        return node.id() === e.target.getParent().id();
      })[0] as Group;

      // すべてのコマを未選択色に設定
      pageComp.setAllKomaColor(false);
      // 選択コマの背景色を設定
      const komaRect: Rect = targetKoma.getChildren(function (node) {
        return node.name() === 'komaRect';
      })[0] as Rect;
      // 選択色を設定
      komaRect.fill(pageComp.SELECTED_COLOR);
      // 選択コマID保持
      pageComp.selectedKomaId = targetKoma.id();

      // キャンセルボタンを活性化
      pageComp.kymw1400KomaPageModel.cancelEnable = true;

      // コマを描画
      pageComp.layerKoma.draw();

      if (pageComp.kymw1400Model.selectedPageId === pageComp.pageId) {
        // モードレスでポップアップしている画面を再表示する。
        pageComp.kymw1400Model.kymw1400Component.sendInputVOToAllPopupDialog();
      }
      // 実績履歴ボタン＆メニュー活性、非活性化処理
      pageComp.kymw1400Model.updateHistoryMenuAndButtonEnable();

      /**
       * コマ入替、コマ詰め処理状態をリセットする。
       */
      function reset() {
        // コマタイプ変更時のリセット処理
        pageComp.kymw1400Model.leftView.resetKomaTypeAction();
        pageComp.kymw1400Model.rightView.resetKomaTypeAction();
      }

      /**
       * 入替元のページを取得する。
       */
      function getFromPage(): PageComponent {
        const leftSelectedKomaId = pageComp.kymw1400Model.leftView.page.selectedKomaId; // 2023.12.27 shuto add
        if (PlessViewUtilsSub.isEmpty(leftSelectedKomaId)) {
          return pageComp.kymw1400Model.rightView.page;
        } else {
          return pageComp.kymw1400Model.leftView.page;
        }
      }

      function changeKomaBackColor(layer: Layer, color: string) {
        // 各レイヤーを取得
        const grp: Group = layer.getChildren(function (node: any) {
          return node.id() === e.target.getParent().id();
        })[0] as Group;
        // 選択コマの背景色を設定
        const rect: Rect = grp.getChildren(function (node: any) {
          return node.name() === 'komaRect';
        })[0] as Rect;
        // 選択色を設定
        rect.fill(color);
        // コマを描画
        layer.draw();
      }

      function eventDeleteKoma_CLOSEHandlerSuccess(): void {
        // 通信成功の場合実行する
        this.kymw1400Model.selectedPage.deleteSelectedKoma();

        // 計画値集計表（計画点数、金額(千円)、GPR、点単価）を再表示する。
        this.dispatchGetPlanSummaryTableEvent();

        // 更新フラグを「削除」に設定
        this.kymw1400Model.updateFlg = 2;

        // 初期化
        this.kymw1400Model.reloadPcodeList = null;

        // モードレスでポップアップしている画面を再表示する。
        this.sendInputVOToAllPopupDialog();
      }
    });

    // ドラッグ中イベント
    // プロキシを表示する
    komaProxyGroup.on('dragmove', function (e: any) {
      // console.log('move: dragmove');
      let targetPage: PageComponent = null;
      let layerKoma: Layer;
      let layerKomaProxy: Layer;
      const mouseEvent: MouseEvent = e.evt;
      dropEnableFlag = false;

      // ページ以外にドラッグしている場合、ゴーストコマを表示しない
      if (e.evt.srcElement.localName !== 'canvas') {
        komaProxyGroup.opacity(0);
        return;
      }
      // 対象ページを取得
      if (e.evt.srcElement.parentElement.parentElement.className === 'left') {
        targetPage = pageComp.kymw1400Model.leftView.page;
      }
      if (e.evt.srcElement.parentElement.parentElement.className === 'right') {
        targetPage = pageComp.kymw1400Model.rightView.page;
      }

      // 別ページフラグを更新
      if (targetPage !== pageComp) {
        pageComp.kymw1400KomaPageModel.otherPageFlag = true;
        layerKoma = targetPage.layerKoma;
        layerKomaProxy = targetPage.layerKomaProxy;
      } else {
        pageComp.kymw1400KomaPageModel.otherPageFlag = false;
        layerKoma = pageComp.layerKoma;
        layerKomaProxy = pageComp.layerKomaProxy;
      }
      // コマ入れ替えフラグ初期化
      targetPage.komaReplaceFlg = false;

      const transformer: Transformer = komaProxyGroup.getParent().getChildren(function (node) {
        return node.id() === e.target.id() && node.name() === 'komaProxyTrans';
      })[0] as Transformer;
      const resizeRect: Rect = komaProxyGroup.getParent().getChildren(function (node) {
        return node.id() === e.target.id() && node.name() === 'komaProxyResizeRect';
      })[0] as Rect;

      if (((targetPage === null || targetPage.kymw1400KomaPageModel.pageLock === true) &&
        pageComp.kymw1400KomaPageModel.otherPageFlag === true) ||
        pageComp.kymw1400KomaPageModel.komaType !== pageComp.kymw1400KomaPageModel.KOMATYPE_MOVE) {
        targetPage.stage.container().style.cursor = 'no-drop';
        komaProxyGroup.opacity(0);
        layerKomaProxy.draw();
        return;
      }

      // 移動開始セクション取得
      const oldSection: SectionComponent = pageComp.getSectionByPosition(pageComp.oldKomaX, pageComp.oldKomaY);
      // 移動中セクション取得
      const section: SectionComponent = targetPage.getSectionByPosition(PlessViewUtils.getLayerX(mouseEvent), PlessViewUtils.getLayerY(mouseEvent));
      if (section === null) {
        targetPage.stage.container().style.cursor = 'no-drop';
        komaProxyGroup.opacity(0);
        layerKomaProxy.draw();
        return;
      }

      // 移動元位置を取得
      const oldPosition = pageComp.getKomaProxyPosition(
        oldSection, pageComp.oldKomaX, pageComp.oldKomaY, pageComp.oldKomaW, pageComp.oldKomaH);

      // 移動元情報から移動後のコマサイズを設定
      let targetKomaW = (section.drawSectionSizeW / section.divX) * (oldPosition.right - oldPosition.left);
      if (targetKomaW > section.drawSectionSizeW) {
        targetKomaW = section.drawSectionSizeW;
      }
      let targetKomaH = (section.drawSectionSizeH / section.divY) * (oldPosition.bottom - oldPosition.top);
      if (targetKomaH > section.drawSectionSizeH) {
        targetKomaH = section.drawSectionSizeH;
      }
      const scaleW = targetKomaW / pageComp.oldKomaW;
      const scaleH = targetKomaH / pageComp.oldKomaH;

      // 移動後の位置を取得
      const position = pageComp.getKomaProxyPosition(
        section, mouseEvent.offsetX - (targetKomaW / 2), mouseEvent.offsetY - (targetKomaH / 2), targetKomaW, targetKomaH);
      const x = section.drawSectionPositionX + (position.left * section.drawSectionSizeW) / section.divX;
      const y = section.drawSectionPositionY + (position.top * section.drawSectionSizeH) / section.divY;

      // 他のコマと重なる場合
      targetPage.collisionFlg = targetPage.isCollisionKoma(e.target.id(), targetPage, section, position);
      if (targetPage.collisionFlg) {
        if (targetPage.komaReplaceFlg) {
          // コマ操作区分の選択項目により、アイコンを設定
          if (targetPage.kymw1400Model.selectedKomaOperation === targetPage.SELECTED_KOMA_REPLACE.toString()) {
            // ページ間移動チェック
            if (pageComp.kymw1400KomaPageModel.otherPageFlag &&
              targetPage.kymw1400Model.selectedCoopCode !== targetPage.kymw1400Model.unSelectedCoopCode) {
              // 現在のページと移動先ページが異なる（ページ間移動）の場合
              // ドラッグアイコンに変更不可アイコンを設定
              targetPage.stage.container().style.cursor = 'no-drop';
            }
          } else {
            // 同一ページかどうかの判定
            if (pageComp.kymw1400KomaPageModel.otherPageFlag) {
              // ドラッグアイコンに変更不可アイコンを設定
              targetPage.stage.container().style.cursor = 'no-drop';
            } else if (((oldPosition.right - oldPosition.left) / oldSection.divX).toFixed(9) !== targetPage.komaTo.sizeW.toFixed(9) ||
              ((oldPosition.bottom - oldPosition.top) / oldSection.divY).toFixed(9) !== targetPage.komaTo.sizeH.toFixed(9)) {
              // 同一コマサイズかどうかの判定
              // ドラッグアイコンに変更不可アイコンを設定
              targetPage.stage.container().style.cursor = 'no-drop';
            }
          }
        } else {
          targetPage.stage.container().style.cursor = 'no-drop';
        }
      } else {
        // ページ間移動チェック
        if (pageComp.kymw1400KomaPageModel.otherPageFlag &&
          targetPage.kymw1400Model.selectedCoopCode !== targetPage.kymw1400Model.unSelectedCoopCode) {
          // 現在のページと移動先ページが異なる（ページ間移動）の場合
          // ドラッグアイコンに変更不可アイコンを設定
          targetPage.stage.container().style.cursor = 'no-drop';
        } else {
          // カーソル設定
          targetPage.stage.container().style.cursor = 'copy';
          dropEnableFlag = true;
        }
      }

      // プロキシ設定
      komaProxyGroup.opacity(0.6);
      komaProxyGroup.x(x);
      komaProxyGroup.y(y);
      komaProxyGroup.scaleX(scaleW);
      komaProxyGroup.scaleY(scaleH);

      // プロキシ背景
      resizeRect.width(komaProxyGroup.width() * komaProxyGroup.scaleX());
      resizeRect.height(komaProxyGroup.height() * komaProxyGroup.scaleY());

      // リサイズオブジェクトを再設定
      pageComp.setResizeDragRange(transformer, komaProxyGroup);

      // コマ描画
      layerKomaProxy.add(resizeRect);
      layerKomaProxy.add(komaProxyGroup);
      layerKomaProxy.add(transformer);
      transformer.setNodes([komaProxyGroup]);
      layerKomaProxy.draw();
    });

    // ドラッグ終了時イベント
    // プロキシを非表示にし、コマを移動後の位置に描画する
    komaProxyGroup.on('dragend', function (e: any) {
      // console.log('move: dragend');
      const mouseEvent: MouseEvent = e.evt;
      let targetPage: PageComponent = null;
      const moveKomaGroup: Group = pageComp.layerKoma.getChildren(function (node) {
        return node.id() === e.target.id();
      })[0] as Group;
      const transformer: Transformer = komaProxyGroup.getParent().getChildren(function (node) {
        return node.id() === e.target.id() && node.name() === 'komaProxyTrans';
      })[0] as Transformer;
      const resizeRect: Rect = komaProxyGroup.getParent().getChildren(function (node) {
        return node.id() === e.target.id() && node.name() === 'komaProxyResizeRect';
      })[0] as Rect;

      if (e.evt.srcElement.parentElement.parentElement.className === 'left') {
        targetPage = pageComp.kymw1400Model.leftView.page;
      }
      if (e.evt.srcElement.parentElement.parentElement.className === 'right') {
        targetPage = pageComp.kymw1400Model.rightView.page;
      }
      // ページ以外にドラッグしている場合、プロキシを表示しない
      if (e.evt.srcElement.localName !== 'canvas') {
        pageComp.kymw1400Model.leftView.page.stage.container().style.cursor = 'auto';
        pageComp.kymw1400Model.rightView.page.stage.container().style.cursor = 'auto';
        rollBackProxy();
        return;
      }

      // 対象ページがロックされている場合処理しない
      if (targetPage.kymw1400KomaPageModel.pageLock === true ||
        targetPage.kymw1400KomaPageModel.komaType !== targetPage.kymw1400KomaPageModel.KOMATYPE_MOVE) {
        rollBackProxy();
        return;
      }

      const section: SectionComponent = targetPage.getSectionByPosition(PlessViewUtils.getLayerX(mouseEvent), PlessViewUtils.getLayerY(mouseEvent));
      if (section === null) {
        rollBackProxy();
        return;
      }

      // ドロップ不可の場合処理しない
      if (dropEnableFlag === false) {
        rollBackProxy();
        return;
      }
      const koma: KomaComponent = pageComp.getKoma(komaProxyGroup.id());

      // 移動先コマが配置されているページ内の商品のＰコードに移動元コマのＰコードが含まれる場合
      if (targetPage.pageId !== pageComp.pageId
        && targetPage.hasPcode(koma.getPcodeList())) {
        // 選択されている媒体の業態区分が'2'(デイリー)以外の場合
        if (targetPage.kymw1400Model.selectedDutiesFormDiv !== '2') {
          rollBackProxy();
          // エラーメッセージを表示する
          setTimeout(() => {
            pageComp.dialogService.openAlertGetMessage('E0118');
          });
          return;
        }
      }

      // 移動開始セクション取得
      const oldSection: SectionComponent = pageComp.getSectionByPosition(pageComp.oldKomaX, pageComp.oldKomaY);
      // 移動元位置を取得
      const oldPosition = pageComp.getKomaProxyPosition(
        oldSection, pageComp.oldKomaX, pageComp.oldKomaY, pageComp.oldKomaW, pageComp.oldKomaH);

      if (targetPage.komaReplaceFlg) {
        // コマ操作区分の選択項目で処理を切り分け
        if (targetPage.kymw1400Model.selectedKomaOperation === targetPage.SELECTED_KOMA_REPLACE.toString()) {
        } else if (!pageComp.kymw1400KomaPageModel.otherPageFlag &&
          ((oldPosition.right - oldPosition.left) / oldSection.divX).toFixed(9) === targetPage.komaTo.sizeW.toFixed(9) &&
          ((oldPosition.bottom - oldPosition.top) / oldSection.divY).toFixed(9) === targetPage.komaTo.sizeH.toFixed(9)) {
        } else {
          rollBackProxy();
        }
      } else if (targetPage.collisionFlg) {
        rollBackProxy();
      } else {
        // プロキシのサイズを調整
        komaProxyGroup.width(komaProxyGroup.width() * komaProxyGroup.scaleX());
        komaProxyGroup.height(komaProxyGroup.height() * komaProxyGroup.scaleY());
        komaProxyGroup.scaleX(1);
        komaProxyGroup.scaleY(1);
        // プロキシ背景
        komaProxyGroup.getChildren()[0].width(komaProxyGroup.width() * komaProxyGroup.scaleX());
        komaProxyGroup.getChildren()[0].height(komaProxyGroup.height() * komaProxyGroup.scaleY());

        // コマサイズを調整
        moveKomaGroup.x(komaProxyGroup.x());
        moveKomaGroup.y(komaProxyGroup.y());
        moveKomaGroup.width(komaProxyGroup.width());
        moveKomaGroup.height(komaProxyGroup.height());

        // コマ背景
        moveKomaGroup.getChildren()[0].width(komaProxyGroup.width());
        moveKomaGroup.getChildren()[0].height(komaProxyGroup.height());

        // 対象ページにコマを追加
        targetPage.layerKoma.add(moveKomaGroup);

        // コマデータを更新
        targetPage.updateKoma(section, komaProxyGroup, pageComp, false);
      }

      // カーソルをもとに戻す
      pageComp.stage.container().style.cursor = 'auto';
      targetPage.stage.container().style.cursor = 'auto';
      document.body.style.cursor = 'auto';
      pageComp.kymw1400KomaPageModel.otherPageFlag = false;

      // プロキシを透明に戻す
      komaProxyGroup.opacity(0);

      // コマ描画
      pageComp.layerKoma.draw();
      pageComp.layerKomaProxy.draw();
      targetPage.layerKoma.draw();
      targetPage.layerKomaProxy.draw();

      /**
       * ドロップ不可の場合プロキシをもとに戻す
       *
       */
      function rollBackProxy() {
        // プロキシ設定
        komaProxyGroup.opacity(0);
        komaProxyGroup.x(moveKomaGroup.x());
        komaProxyGroup.y(moveKomaGroup.y());
        komaProxyGroup.width(moveKomaGroup.width());
        komaProxyGroup.height(moveKomaGroup.height());
        komaProxyGroup.scaleX(1);
        komaProxyGroup.scaleY(1);

        // プロキシ背景
        resizeRect.width(komaProxyGroup.width());
        resizeRect.height(komaProxyGroup.height());

        // リサイズオブジェクトを再設定
        pageComp.setResizeDragRange(transformer, komaProxyGroup);

        // コマ描画
        pageComp.layerKomaProxy.add(resizeRect);
        pageComp.layerKomaProxy.add(komaProxyGroup);
        pageComp.layerKomaProxy.add(transformer);
        transformer.setNodes([komaProxyGroup]);
        pageComp.stage.container().style.cursor = 'auto';
        pageComp.layerKomaProxy.draw();
        if (targetPage != null) {
          targetPage.stage.container().style.cursor = 'auto';
          targetPage.layerKomaProxy.draw();
        }
      }
    });
  }

  /**
   * コマに対してリサイズイベントを追加する
   *
   * @param {Transformer} transformer
   * @param {PageComponent} pageComp
   * @memberof PageComponent
   */
  addResizeEvent(transformer: Transformer, group: Group, rect: Rect, pageComp: PageComponent): void {
    let updateFlg = Boolean(false);

    // // ドラッグ開始時イベント
    // コマの背景色を設定する
    transformer.on('mousedown', function (e: any) {
      /** コマ情報のバックアップ(選択時) **/
      pageComp.backupOldKoma(group);

      // ページに選択コマ(自分)IDを通知して他のコマを非選択状態にする
      pageComp.setSelectedKoma(e.target.getParent().id());
      // 各レイヤーを取得
      const targetKoma: Group = pageComp.layerKoma.getChildren(function (node) {
        return node.id() === e.target.getParent().id();
      })[0] as Group;

      // すべてのコマを未選択色に設定
      pageComp.setAllKomaColor(false);
      // 選択コマの背景色を設定
      const komaRect: Rect = targetKoma.getChildren(function (node) {
        return node.name() === 'komaRect';
      })[0] as Rect;
      // 選択色を設定
      komaRect.fill(pageComp.SELECTED_COLOR);
      // 選択コマID保持
      pageComp.selectedKomaId = targetKoma.id();

      // キャンセルボタンを活性化
      pageComp.kymw1400KomaPageModel.cancelEnable = true;

      // コマを描画
      pageComp.layerKoma.draw();

      if (pageComp.kymw1400Model.selectedPageId === pageComp.pageId) {
        // モードレスでポップアップしている画面を再表示する。
        pageComp.kymw1400Model.kymw1400Component.sendInputVOToAllPopupDialog();
      }
      // 実績履歴ボタン＆メニュー活性、非活性化処理
      pageComp.kymw1400Model.updateHistoryMenuAndButtonEnable();
    });

    transformer.on('transform', function (e: any) {
      const mouseEvent: MouseEvent = e.evt;
      updateFlg = false;
      pageComp.stage.container().style.cursor = 'auto';
      pageComp.layerKomaFrame.getCanvas()._canvas.style.cursor = '';
      document.body.style.cursor = 'auto';

      // 右クリックで通らないための条件
      if (mouseEvent.buttons === 1) {

        // プロキシを半透明で表示
        rect.opacity(0.5);
        // サブ画面表示時（データ取得後）の最初の一回はイベントたーげとをうまく取得できないため無視する。
        if (e.evt.srcElement.parentElement.className === 'modal-open') {
          return;
        }
        if (e.evt.srcElement.localName !== 'canvas') {
          pageComp.stage.container().style.cursor = 'no-drop';
          rect.opacity(0);
          pageComp.layerKomaProxy.draw();
          return;
        }

        // サイズ変更時の種類を取得("top-left"など)
        const transType = e.currentTarget._movingAnchorName;

        // ドラッグ開始セクション取得
        const oldSection: SectionComponent = pageComp.getSectionByPosition(pageComp.oldKomaX, pageComp.oldKomaY);

        // リサイズ中セクション取得
        const section: SectionComponent = pageComp.getSectionByPosition(mouseEvent.offsetX, mouseEvent.offsetY);
        if (section === null) {
          pageComp.stage.container().style.cursor = 'no-drop';
          rect.opacity(0);
          pageComp.layerKomaProxy.draw();
          return;
        }

        // 同一セクションではない場合処理しない
        if (oldSection !== section) {
          pageComp.stage.container().style.cursor = 'no-drop';
          rect.opacity(0);
          pageComp.layerKomaProxy.draw();
          return;
        }

        const tempKoma = pageComp.getKoma(group.id());

        // 吸着処理を行った場合の座標を取得
        const position = pageComp.getSectionProxyPositionForResize(section,
          pageComp.getMousePosition(section, mouseEvent.offsetX, mouseEvent.offsetY),
          tempKoma,
          transType);

        // 他のコマと重なる場合
        if (pageComp.isCollisionKoma(group.id(), pageComp, section, position)) {
          pageComp.layerKomaFrame.getCanvas()._canvas.style.cursor = 'no-drop';
          document.body.style.cursor = 'no-drop';
          rect.opacity(0);
          pageComp.layerKomaProxy.draw();
          return;
        }

        // 矩形を描写する
        const x = section.drawSectionPositionX +
          CalcUtil.multiply(position.left.toString(), section.drawSectionSizeW.toString()) / section.divX;
        const y = section.drawSectionPositionY +
          CalcUtil.multiply(position.top.toString(), section.drawSectionSizeH.toString()) / section.divY;
        const width = CalcUtil.multiply(
          CalcUtil.subtract(position.right.toString(), position.left.toString()).toString(),
          section.drawSectionSizeW.toString())
          / section.divX;
        const height = CalcUtil.multiply(
          CalcUtil.subtract(position.bottom.toString(), position.top.toString()).toString(),
          section.drawSectionSizeH.toString())
          / section.divY;
        rect.x(x);
        rect.y(y);
        rect.width(width);
        rect.height(height);

        // 更新フラグを設定
        updateFlg = true;
      }
    });

    // ドラッグ終了時イベント
    // プロキシを非表示にし、コマのサイズを変更する
    transformer.on('transformend', function (e: any) {
      const mouseEvent: MouseEvent = e.evt;
      const resizeKomaProxyGroup: Group = e.target as Group;

      // 右クリックで動作しないための条件
      if (mouseEvent.button !== 2) {
        // カーソルをもとに戻す
        document.body.style.cursor = 'auto';
        pageComp.layerKomaFrame.getCanvas()._canvas.style.cursor = '';
        pageComp.stage.container().style.cursor = 'auto';

        // 更新フラグがfalseの場合処理しない
        if (!updateFlg) {
          pageComp.reDraw();
          return;
        }

        // ページ以外にドラッグしている場合、プロキシを表示しない
        if (e.evt.srcElement.localName !== 'canvas') {
          pageComp.reDraw();
          return;
        }
        // セクション取得
        const section: SectionComponent = pageComp.getSectionByPosition(PlessViewUtils.getLayerX(mouseEvent), PlessViewUtils.getLayerY(mouseEvent));
        if (section === null) {
          pageComp.reDraw();
          return;
        }

        resizeKomaProxyGroup.x(rect.x());
        resizeKomaProxyGroup.y(rect.y());
        resizeKomaProxyGroup.width(rect.width());
        resizeKomaProxyGroup.height(rect.height());

        // プロキシを透明に戻す
        rect.opacity(0);

        // コマデータを更新
        pageComp.updateKoma(section, resizeKomaProxyGroup, pageComp, true);
      }
    });
  }

  /**
   * コンテキストメニューを追加
   *
   * @param {Group} target 対象コマ
   * @param {PageComponent} pageComp セクションコンポーネント
   * @memberof PageComponent
   */
  setContextMenu(target: any, pageComp: PageComponent): void {
    const div: HTMLElement = document.createElement('div');
    // コンテキストメニュー生成
    pageComp.contextMenu = new Menu(div, {
      displayMemberPath: 'header',
      selectedValuePath: 'cmd',
      itemsSource: pageComp.getMenuItems(),
      command: pageComp.getFormatMenuCommand(),
      // イベント設定
      itemClicked: function (s, e) {
        switch (pageComp.contextMenu.selectedValue) {
          case 'GoodsInfo':
            pageComp.eventGoodsInfo_MENU_SELECTHandler();
            break;
          case 'ImageDirection':
            pageComp.eventImageDirection_MENU_SELECTHandler();
            break;
          case 'MarkDirection':
            pageComp.eventMarkDirection_MENU_SELECTHandler();
            break;
          case 'History':
            pageComp.eventHistory_MENU_SELECTHandler();
            break;
          case 'GoodsMentenance':
            pageComp.eventGoodsMentenance_MENU_SELECTHandler();
            break;
          case 'MemberVoiceSelect':
            pageComp.eventMemberVoiceSelect_MENU_SELECTHandler();
            break;
          case 'MemberVoiceDelete':
            pageComp.eventMemberVoiceDelete_MENU_SELECTHandler();
            break;
          case 'DeleteKoma':
            pageComp.eventDeleteKoma_MENU_SELECTHandler();
            break;
          case 'OpenKYMW1422':
            pageComp.eventOpenKYMW1422_CLICKHandler();
            break;
        }
      }
    });

    // コンテキストメニューを表示する
    target.on('contextmenu', function (e) {
      e.evt.preventDefault();
      const koma: KomaComponent = pageComp.getKoma(pageComp.selectedKomaId);
      pageComp.eventContextMenu_Handler(koma, e);
    });

    // コンテキストメニューを非表示にする
    pageComp.stage.on('click', function (e) {
      e.evt.preventDefault();
      pageComp.contextMenu.hide();
    });
    pageComp.otherPageComp.stage.on('click', function (e) {
      e.evt.preventDefault();
      pageComp.contextMenu.hide();
    });
  }

  /**
   * コンテキストメニューの項目を取得する
   *
   * @private
   * @returns {Array<string>}
   * @memberof PageComponent
   */
  private getMenuItems(): Array<string> {
    const menuItems = [];
    menuItems.push({ header: '商品情報', cmd: 'GoodsInfo' });
    menuItems.push({ header: '画像指示', cmd: 'ImageDirection' });
    menuItems.push({ header: 'マーク指示', cmd: 'MarkDirection' });
    menuItems.push({ header: '実績履歴', cmd: 'History' });
    menuItems.push({ header: '商品企画メンテナンス', cmd: 'GoodsMentenance' });
    menuItems.push({ header: '組合員の声選択', cmd: 'MemberVoiceSelect' });
    menuItems.push({ header: '-' });
    menuItems.push({ header: '組合員の声削除', cmd: 'MemberVoiceDelete' });
    menuItems.push({ header: '-' });
    menuItems.push({ header: 'このコマを削除', cmd: 'DeleteKoma' });
    menuItems.push({ header: '-' });
    menuItems.push({ header: 'W掲載元(親)設定', cmd: 'OpenKYMW1422' });
    return menuItems;
  }

  /**
   * メニュー項目活性・非活性制御
   *
   * @private
   * @returns {*}
   * @memberof PageComponent
   */
  private getFormatMenuCommand(): any {
    return {
      canExecuteCommand: (param) => {
        // 各項目の活性制御
        switch (param.cmd) {
          case 'GoodsInfo':
            return !this.disabledGoodsInfo;
          case 'ImageDirection':
            return !this.disabledImageDirection;
          case 'MarkDirection':
            return !this.disabledMarkDirection;
          case 'History':
            return !this.disabledHistory;
          case 'GoodsMentenance':
            return !this.disabledGoodsMentenance;
          case 'MemberVoiceSelect':
            return !this.disabledMemberVoiceSelect;
          case 'MemberVoiceDelete':
            return !this.disabledMemberVoiceDelete;
          case 'DeleteKoma':
            return !this.disabledDeleteKoma;
          case 'OpenKYMW1422':
            return !this.disabledOpenKYMW1422;
          default:
            return true;
        }
      }
    };
  }

  /**
   * 右クリックメニューが表示されたときに呼び出されるメソッド
   * 主に右クリックされた行の選択と、メニュー項目の表示設定を行う
   *
   * @param event:コンテキストイベント
   */
  public eventContextMenu_Handler(koma: KomaComponent, e: any): void {
    // 全て活性
    this.disabledGoodsInfo = false;
    this.disabledImageDirection = false;
    this.disabledMarkDirection = false;
    this.disabledHistory = false;
    this.disabledGoodsMentenance = false;
    this.disabledMemberVoiceSelect = false;
    this.disabledMemberVoiceDelete = false;
    this.disabledDeleteKoma = false;
    this.disabledOpenKYMW1422 = false;
    const blankKomaFlg: boolean = koma.goods.pcodeList.length === 0;
    if (blankKomaFlg) {
      this.disabledGoodsMentenance = true;
      this.disabledHistory = true;
      this.disabledMemberVoiceSelect = true;
      this.disabledMemberVoiceDelete = true;
      this.disabledOpenKYMW1422 = true;
    }

    if (this.kymw1400Model.funcPermission === '1') {
      this.disabledDeleteKoma = true;
      this.disabledMemberVoiceDelete = true;
      this.disabledOpenKYMW1422 = true;
    }

    if (this.kymw1400Model.selectedPageManage.pageLock ||
      this.kymw1400KomaPageModel.komaType !== this.kymw1400KomaPageModel.KOMATYPE_MOVE) {
      this.disabledDeleteKoma = true;
      this.disabledMemberVoiceDelete = true;
      this.disabledOpenKYMW1422 = true;
    }

    // W掲載商品がコマに掲載されていない場合、W掲載元(親)設定は非活性
    if (!this.hasWPublishedGoods(koma.goods.layoutInfo)) {
      this.disabledOpenKYMW1422 = true;
    }

    // コンテキストメニュー表示
    this.contextMenu.show(e.evt);
  }

  private hasWPublishedGoods(goodsList: { MultiPublishString: string; }[]) {
    return (goodsList == null) ? false : goodsList.some(goods => this.isWPublishedGoods(goods.MultiPublishString));
  }

  /**
   * 商品情報コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventGoodsInfo_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.eventGoodsInfo_CLICKHandler();
  }

  /**
   * 画像指示コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventImageDirection_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.eventImageDirection_CLICKHandler();
  }

  /**
   * マーク表示コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventMarkDirection_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.eventMarkDirection_CLICKHandler();
  }

  /**
   * 実績履歴コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventHistory_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.eventHistory_CLICKHandler();
  }

  /**
   * 商品企画メンテナンスコンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventGoodsMentenance_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.openPopUpKYMW0900();
  }

  /**
   * 組合員の声選択コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventMemberVoiceSelect_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.openPopUpKYMW0A00();
  }

  /**
   * 組合員の声削除コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventMemberVoiceDelete_MENU_SELECTHandler(): void {
    // // 削除確認のメッセージを表示する。「NO」が選択された場合は処理を終了する。
    this.dialogService.openAlertGetMessage(
      'Q0106',
      ['組合員の声'],
      (closeDetail) => {
        this.eventMemberVoiceDelete_CLOSEHandlerYesNo(closeDetail);
      });
  }

  /**
   * 組合員の声削除アラートクローズイベントYESNO
   *
   * @private
   * @param {CloseEvent} e
   * @memberof KomaComponent
   */
  private eventMemberVoiceDelete_CLOSEHandlerYesNo(closeDetail: number): void {
    if (closeDetail === PlessAlert.Yes) {
      // 組合員の声掲載削除要求(/KYMW14/registPublishVoice.xml)をサーバに送信する。
      const vo = this.kymw1400Model.getKomaIDVO();
      this.deletePublishVoiceService.execute(
        vo,
        () => {
          this.eventMemberVoiceDelete_CLOSEHandlerSuccess();
        });
    }
  }

  /**
   * 組合員の声削除アラートクローズイベント成功時
   *
   * @private
   * @memberof KomaComponent
   */
  private eventMemberVoiceDelete_CLOSEHandlerSuccess(): void {
    // // モードレスでポップアップしている画面を再表示する。（組合員の声選択）
    this.kymw1400Model.kymw1400Component.sendInputVOToAllPopupDialog(false, false, false, false, false, true);
  }

  /**
   * このコマを削除コンテキストメニューイベント
   *
   * @private
   * @param {Event} e
   * @memberof KomaComponent
   */
  public eventDeleteKoma_MENU_SELECTHandler(): void {
    this.kymw1400Model.kymw1400Component.eventDeleteKoma_CLICKHandler();
  }

  public eventOpenKYMW1422_CLICKHandler() {
    this.kymw1400Model.kymw1400Component.openKYMW1422();
  }

  /**
   * 移動時(吸着処理後)のコマプロキシの座標(ページの左上を(0,0)、右下を(X分割数,Y分割数))を取得する
   * @param x 移動後のx位置
   * @param y 移動後のy位置
   * @param width 移動するコマの幅
   * @param height 移動するコマの高さ
   */
  private getKomaProxyPosition(section: SectionComponent, x: number, y: number, width: number, height: number): any {
    // セクション内での座標を取得
    let top = Number(y - section.drawSectionPositionY);
    let left = Number(x - section.drawSectionPositionX);
    // 上と左の座標を取得する(誤差を少なくする為、グリッドの最小単位0.25を1として一旦算出)
    top = Number(CalcUtil.round((top / (section.drawSectionSizeH / section.divY) * this.GRID_DIV_NUM).toString(), 2));
    left = Number(CalcUtil.round((left / (section.drawSectionSizeW / section.divX) * this.GRID_DIV_NUM).toString(), 2));
    // グリッドの最小単位0.25を掛けて、本来の数値に戻す)
    top = CalcUtil.multiply(Math.floor(top).toString(), this.GRID_SIZE_MIN.toString());
    left = CalcUtil.multiply(Math.floor(left).toString(), this.GRID_SIZE_MIN.toString());
    if (top < 0) {
      top = 0;
    }
    if (left < 0) {
      left = 0;
    }
    // 上・左の値にそれぞれ幅・高さを足して下・右の座標を算出
    const bottom = CalcUtil.add(top.toString(), CalcUtil.round((height / (section.drawSectionSizeH / section.divY)).toString(), 2));
    const right = CalcUtil.add(left.toString(), CalcUtil.round((width / (section.drawSectionSizeW / section.divX)).toString(), 2));
    // ページ領域からはみ出さないように修正
    if (bottom > section.divY) {
      top = CalcUtil.subtract(
        top.toString(),
        CalcUtil.subtract(bottom.toString(), section.divY.toString()).toString());
    }
    if (right > section.divX) {
      left = CalcUtil.subtract(
        left.toString(),
        CalcUtil.subtract(right.toString(), section.divX.toString()).toString());
    }

    return {
      top: top,
      left: left,
      bottom: bottom,
      right: right,
    };
  }

  /**
 * サイズ変更時(吸着処理後)のコマプロキシの座標(ページの左上を(0,0)、右下を(X分割数,Y分割数))を取得する
 * @param mousePosition マウスポインタのあるグリッドの座標情報
 * @param sectionTemplatePosition 変更対象のコマの座標情報(変更前の座標)
 * @param type type 変更の種類(top-leftなど)
 */
  private getSectionProxyPositionForResize(section: SectionComponent, mousePosition: any, koma: KomaComponent, type: string): any {

    // 変更前の座標値を一旦設定
    let top = koma.positionY * section.divY;
    let left = koma.positionX * section.divX;
    let bottom = (koma.positionY + koma.sizeH) * section.divY;
    let right = (koma.positionX + koma.sizeW) * section.divX;

    // 上下のサイズ変更
    if (type.indexOf('top') > -1) {             // 上からドラッグした場合
      if (bottom < mousePosition.bottom) {   // 上下反転した場合
        top = CalcUtil.subtract(bottom.toString(), this.GRID_SIZE_MIN.toString());  // 最小グリッド分残す
        bottom = mousePosition.bottom;
      } else {                            // 通常の操作
        top = mousePosition.top;
      }
    } else if (type.indexOf('bottom') > -1) {   // 下からドラッグした場合
      if (top > mousePosition.top) {   // 上下反転した場合
        bottom = CalcUtil.add(top.toString(), this.GRID_SIZE_MIN.toString());       // 最小グリッド分残す
        top = mousePosition.top;
      } else {                            // 通常の操作
        bottom = mousePosition.bottom;
      }
    }
    // サイズが同じ(高さが0)になる場合、グリッドの最小サイズ(0.5)は残す
    if (top === bottom) {
      if (type.indexOf('top') > -1) {
        top = CalcUtil.subtract(top.toString(), this.GRID_SIZE_MIN.toString());
      } else {
        bottom = CalcUtil.add(bottom.toString(), this.GRID_SIZE_MIN.toString());
      }
    }

    // 左右のサイズ変更
    if (type.indexOf('left') > -1) {            // 左からドラッグした場合
      if (right < mousePosition.right) {   // 反転した場合
        left = CalcUtil.subtract(right.toString(), this.GRID_SIZE_MIN.toString());
        right = mousePosition.right;
      } else {                            // 通常の操作
        left = mousePosition.left;
      }
    } else if (type.indexOf('right') > -1) {    // 右からドラッグした場合
      if (left > mousePosition.left) {   // 反転した場合
        right = CalcUtil.add(left.toString(), this.GRID_SIZE_MIN.toString());
        left = mousePosition.left;
      } else {                            // 通常の操作
        right = mousePosition.right;
      }
    }
    // サイズが同じ(幅が0)になる場合、グリッドの最小サイズ(0.5)は残す
    if (left === right) {
      if (type.indexOf('left') > -1) {
        left = CalcUtil.subtract(left.toString(), this.GRID_SIZE_MIN.toString());
      } else {
        right = CalcUtil.add(right.toString(), this.GRID_SIZE_MIN.toString());
      }
    }

    // ページ領域からはみ出さないように修正した値を返す
    return {
      top: (top < 0) ? 0 : top,
      left: (left < 0) ? 0 : left,
      bottom: (bottom > section.divY) ? section.divY : bottom,
      right: (right > section.divX) ? section.divX : right,
    };
  }

  /**
   * マウスポインタのあるグリッドの位置をページの座標で取得する（グリッド分割）
   * @param x ページ上のマウスのX座標
   * @param y ページ上のマウスのY座標
   */
  private getMousePosition(section: SectionComponent, x: number, y: number) {
    // セクション内での座標を取得
    let top = Number(y - section.drawSectionPositionY);
    let left = Number(x - section.drawSectionPositionX);
    // 上と左の座標を取得する(誤差を少なくする為、グリッドの最小単位0.5を1として一旦算出)
    top = Number(CalcUtil.round((top / (section.drawSectionSizeH / section.divY) * this.GRID_DIV_NUM).toString(), 2));
    left = Number(CalcUtil.round((left / (section.drawSectionSizeW / section.divX) * this.GRID_DIV_NUM).toString(), 2));
    // グリッドの最小単位0.5を掛けて、本来の数値に戻す)
    top = CalcUtil.multiply(Math.floor(top).toString(), this.GRID_SIZE_MIN.toString());
    left = CalcUtil.multiply(Math.floor(left).toString(), this.GRID_SIZE_MIN.toString());
    // グリッドの最小単位0.5を足して下と右の座標を取得する
    const bottom = CalcUtil.add(top.toString(), this.GRID_SIZE_MIN.toString());
    const right = CalcUtil.add(left.toString(), this.GRID_SIZE_MIN.toString());
    return {
      top: (top < 0) ? 0 : top,
      left: (left < 0) ? 0 : left,
      bottom: (bottom > section.divY) ? section.divY : bottom,
      right: (right > section.divX) ? section.divX : right,
    };
  }

  /**
   * ロック処理を行う。
   * @param lockFlg:ロックフラグ
   */
  public set lock(lockFlg: boolean) {
    // コマの移動制御
    const komaProxyGrps = this.layerKomaProxy.getChildren(function (node) {
      return node.name() === 'komaProxyGrp';
    });
    for (let i = Number(0); i < komaProxyGrps.length; i++) {
      komaProxyGrps[i].draggable(!lockFlg);
    }

    // コマのリサイズ制御
    const komaProxyTrans = this.layerKomaProxy.getChildren(function (node) {
      return node.name() === 'komaProxyTrans';
    }) as Transformer[];
    for (let i = Number(0); i < komaProxyTrans.length; i++) {
      komaProxyTrans[i].resizeEnabled(!lockFlg);
    }
    this.layerKomaProxy.draw();
  }

  /**
   * 選択したコマの通知を受けて再度表示を指示する
   * @param komaId:コマID
   */
  public setSelectedKoma(komaId: string): void {
    this.sectionList.forEach(section => {
      section.setSelectedKoma(komaId);
    });
  }

  /**
   * フォントサイズを設定する
   *
   * @param fontSize:ScaleXフォントサイズ
   */
  public setFontSize(): void {
    if (!PlessViewUtilsSub.isEmpty(this.pageId)) {
      this.reDraw();
    }
  }

  /**
   * コマに画像フラグを設定する
   *
   * @param komaImageViewFlg:コマに画像表示フラグ
   */
  public setKomaImageViewFlg(): void {
    if (!PlessViewUtilsSub.isEmpty(this.pageId)) {
      this.reDraw();
    }
  }

  /**
   * コマに文字情報を設定する
   *
   * @param komaStringView:コマに文字情報表示フラグ
   */
  public setKomaTextViewFlg(): void {
    if (!PlessViewUtilsSub.isEmpty(this.pageId)) {
      this.reDraw();
    }
  }

  /**
   * 新しいコマを追加する
   *
   * @return コマ配置可否
   */
  public addNewKoma(): boolean {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].addNewKoma(
        this.registKomaService,
        (koma: KomaComponent) => {
          koma.komaData.KomaID = koma.komaId;
          koma.komaId = koma.komaId;
          koma.drawKomaPositionX = this.sectionList[i].drawSectionPositionX
            + koma.positionX * this.sectionList[i].drawSectionSizeW;
          koma.drawKomaPositionY = this.sectionList[i].drawSectionPositionY
            + koma.positionY * this.sectionList[i].drawSectionSizeH;
          koma.drawKomaSizeW = koma.sizeW * this.sectionList[i].drawSectionSizeW;
          koma.drawKomaSizeH = koma.sizeH * this.sectionList[i].drawSectionSizeH;
          this.drawKoma(koma);
        })
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * 新しいコマ商品を追加する
   */
  public addNewGoodsKoma(pCodeList: Array<any>): boolean {
    // 全ての空白コマを取得する
    const komaInfoVOList: Array<any> = new Array<any>();
    this.sectionList.forEach(section => {
      section.getAllEmptyKoma().forEach(koma => {
        komaInfoVOList.push(this.kymw1400Model.getKomaInfoVO(section, koma));
      });
    });

    // コマ追加可能チェック
    if (komaInfoVOList.length < pCodeList.length) {
      return false;
    }

    // Pコード設定
    const paramKomaInfoVOList: Array<any> = new Array<any>();
    for (let i = Number(0); i < pCodeList.length; i++) {
      komaInfoVOList[i].pCode = String(pCodeList[i].pcode);
      komaInfoVOList[i].receiptSOrderCode = String(pCodeList[i].receiptSOrderCode);
      komaInfoVOList[i].startOrderCode = String(pCodeList[i].startOrderCode);
      komaInfoVOList[i].orderNum6Digits = String(pCodeList[i].orderNum6Digits);
      komaInfoVOList[i].itemCode = String(pCodeList[i].itemCode);
      komaInfoVOList[i].orderGoodsFlg = String(pCodeList[i].orderGoodsFlg);

      paramKomaInfoVOList.push(komaInfoVOList[i]);
    }

    // イベント発行
    const vo = this.kymw1400Model.getRegistKomaGoodsVO(paramKomaInfoVOList);
    this.registKomaGoodsService.execute(this.kymw1400Model, vo, pCodeList, (pCodeListData) => {
      this.kymw1400Model.kymw1400Component.setKYMW1404ParameterSuccess1(pCodeListData);
    });

    return true;
  }

  /**
   * 選択コマ情報を削除する
   */
  public deleteSelectedKoma(): boolean {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].deleteSelectedKoma()) {
        this.reDraw();
        return true;
      }
    }
    return false;
  }

  /**
   * 全コマ情報を削除する
   */
  public deleteAllKoma(): void {
    this.sectionList.forEach(section => {
      section.deleteAllKoma();
    });
    this.reDraw();
  }

  /**
   * コマが選択されているのか
   *
   * @return コマ選択
   */
  public isSelectedKoma(): boolean {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].isSelectedKoma()) {
        return true;
      }
    }
    return false;
  }

  /**
   * コマ表示非表示設定
   *
   * @param showFlg:コマ表示、非表示フラグ
   */
  public komaShow(): void {
    if (!PlessViewUtilsSub.isEmpty(this.pageId)) {
      this.reDraw();
    }
  }

  /**
   * ページ情報をクリアする
   */
  public clear(): void {
    // ページ
    this.layerPage.clear();
    this.layerPage.removeChildren();
    this.layerPage.draw();
    // セクション
    this.layerSection.clear();
    this.layerSection.removeChildren();
    this.layerSection.draw();
    // コマ
    this.layerKoma.clear();
    this.layerKoma.removeChildren();
    this.layerKoma.draw();
    // コマプロキシ
    this.layerKomaProxy.clear();
    this.layerKomaProxy.removeChildren();
    this.layerKomaProxy.draw();

    this.sectionList.length = 0;

    this.selectedKomaId = '';
    this.pageId = '';
    this.width = 0;
    this.height = 0;
    this.divX = 1;
    this.divY = 1;

    // キャンセルボタンを非活性設定
    this.kymw1400KomaPageModel.cancelEnable = false;
  }

  /**
   * Pコードが存在するかチェックを行う
   */
  public hasPcode(pcodeList: Array<string>): boolean {
    for (let i = Number(0); i < pcodeList.length; i++) {
      for (let j = Number(0); j < this.sectionList.length; j++) {
        if (this.sectionList[j].hasPcode(pcodeList[i])) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * 移動前のコマの情報をバックアップする
   *
   * @param {Group} target
   * @memberof PageComponent
   */
  private backupOldKoma(target: Group) {
    this.oldKomaX = target.x();
    this.oldKomaY = target.y();
    this.oldKomaW = target.width();
    this.oldKomaH = target.height();
  }

  /**
   * すべてのコマの背景色を未選択色に設定
   *
   * @param {boolean} drawFlag TRUE:描画する、FALSE:描画しない
   * @memberof PageComponent
   */
  setAllKomaColor(drawFlag: boolean) {
    const komaList: any[] = this.layerKoma.getChildren(function (node) {
      return node.getClassName() === 'Group';
    });
    let i = 0;
    // 対象ページのコマの背景色を設定
    for (i = 0; i < komaList.length; i++) {
      const komaRect: Rect = komaList[i].getChildren(function (node) {
        return node.name() === 'komaRect';
      })[0];
      // 未選択コマ背景色
      komaRect.fill(this.NO_SELECTED_COLOR);
    }

    if (drawFlag) {
      this.layerKoma.draw();
    }
  }

  /**
   * 対象コマグループ内のコマに対して、衝突判定を行う
   *
   * @param {string} komaId 衝突判定対象コマID（新規追加時はnull）
   * @param {number} komaPosX 衝突判定対象コマ位置X
   * @param {number} komaPosY 衝突判定対象コマ位置Y
   * @param {number} komaWidth 衝突判定対象コマ幅
   * @param {number} komaHeight 衝突判定対象コマ高さ
   * @param {any[]} komaGrp 衝突判定対象コマグループ
   * @returns {boolean} true:衝突, false:衝突していない
   * @memberof PageComponent
   */
  isCollisionKoma(komaId: string, pageComp: PageComponent, section: SectionComponent, tempKomaPosition: any): boolean {
    let i: number;
    let collisionFlg = Boolean(false);
    for (i = 0; i < section.komaList.length; i++) {
      const position = pageComp.getKomaProxyPosition(
        section, section.komaList[i].drawKomaPositionX, section.komaList[i].drawKomaPositionY,
        section.komaList[i].drawKomaSizeW, section.komaList[i].drawKomaSizeH);

      if (!(Number(tempKomaPosition.left.toFixed(4)) >= Number(position.right.toFixed(4)) ||
        Number(tempKomaPosition.right.toFixed(4)) <= Number(position.left.toFixed(4))
        || Number(tempKomaPosition.top.toFixed(4)) >= Number(position.bottom.toFixed(4)) ||
        Number(tempKomaPosition.bottom.toFixed(4)) <= Number(position.top.toFixed(4)))) {
        if (komaId === null || komaId !== section.komaList[i].komaId) {
          if (tempKomaPosition.left === position.left && tempKomaPosition.top === position.top) {
            this.komaReplaceFlg = true;
            this.komaTo = this.getKoma(section.komaList[i].komaId);
          }
          collisionFlg = true;
        }
      }
    }
    return collisionFlg;
  }

  /**
   * コマデータ更新
   *
   * @param {Group} target
   * @param {Group} originKoma
   * @memberof PageComponent
   */
  updateKoma(section: SectionComponent, koma: Group, pageComp: PageComponent, isChangeKomaSize: boolean): void {
    const komaComp = pageComp.getKoma(koma.id());
    const komaPosition = this.getKomaProxyPosition(section, koma.x(), koma.y(), koma.width(), koma.height());
    const tempKoma: KomaComponent = new KomaComponent;
    tempKoma.setData(komaComp.komaData);
    tempKoma.positionX = komaPosition.left / section.divX;
    tempKoma.positionY = komaPosition.top / section.divY;
    tempKoma.sizeW = (komaPosition.right - komaPosition.left) / section.divX;
    tempKoma.sizeH = (komaPosition.bottom - komaPosition.top) / section.divY;
    tempKoma.page = section.page;
    tempKoma.section = section;

    // リクエストパラメータの設定
    // コマ編集VO情報取得
    const vo = this.kymw1400Model.getUpdateKomaVO(tempKoma);

    // // サーバにデータ取得を要求(Cairngormイベント発行)
    this.updateKomaService.execute(vo,
      () => {
        this.dragDropHandlerSuccess1(section, komaComp, tempKoma, isChangeKomaSize);
      });
  }

  /**
   * コマ入れ替え処理
   *
   * @private
   * @param {KomaComponent} komaFrom 入替元のコマ
   * @param {KomaComponent} komaTo 入替先のコマ
   * @memberof PageComponent
   */
  private replaceKoma(komaFrom: KomaComponent, komaTo: KomaComponent) {
    // リクエストパラメータの設定
    const vo = this.kymw1400Model.getReplaceKomaVO(komaFrom, komaTo);									// コマ入替VO情報取得
    this.replaceKomaService.execute(vo,
      () => {
        this.dragDropHandlerSuccess3();
      });
  }

  /**
   * コマ詰め処理
   *
   * @private
   * @param {Group} komaGroup
   * @memberof PageComponent
   */
  private shiftKoma(komaFrom: KomaComponent, komaTo: KomaComponent) {
    // リクエストパラメータの設定
    const vo = this.kymw1400Model.getShiftKomaVO(komaFrom, komaTo);									// コマ入替VO情報取得
    this.shiftKomaService.execute(vo,
      () => {
        this.dragDropHandlerSuccess3();
      });
  }

  /**
   * オブジェクトをDropした時呼ばれるイベントでサーバ通信が成功したら呼ばれるメソッド
   */
  private dragDropHandlerSuccess1(section: SectionComponent, koma: KomaComponent, tempKoma: KomaComponent,
    isChangeKomaSize: boolean): void {
    // コマ情報を移動先コマへ移動
    section.moveKomaData(koma, tempKoma);

    // 選択コマを設定する
    this.setSelectedKoma(koma.komaId);

    // 再描画
    this.reDraw();

    // 計画値集計表（計画点数、金額(千円)、GPR、点単価）を再表示する。
    this.kymw1400Model.kymw1400Component.dispatchGetPlanSummaryTableEvent(true);

    // ページ間移動時のみ実施
    if (!isChangeKomaSize) {
      // 移動元ページのキャンセルボタンを非活性に、移動先ページのキャンセルボタンを活性に設定
      this.kymw1400Model.leftView.page.selectedKomaId = this.kymw1400Model.leftView.page.selectedKoma ?
        this.kymw1400Model.leftView.page.selectedKoma.komaId : '';

      this.kymw1400Model.rightView.page.selectedKomaId = this.kymw1400Model.rightView.page.selectedKoma ?
        this.kymw1400Model.rightView.page.selectedKoma.komaId : '';

      // 移動元ページのキャンセルボタンを非活性に、移動先ページのキャンセルボタンを活性に設定
      this.kymw1400Model.leftView.kymw1400KomaPageModel.cancelEnable =
        this.kymw1400Model.leftView.page.selectedKomaId !== '';
      this.kymw1400Model.rightView.kymw1400KomaPageModel.cancelEnable =
        this.kymw1400Model.rightView.page.selectedKomaId !== '';
    }
  }

  /**
   * オブジェクトをDropした時呼ばれるイベントでサーバ通信が成功したら呼ばれるメソッド
   */
  private dragDropHandlerSuccess2(): void {
    this.kymw1400Model.kymw1400Component.sendInputVOToKYMW1404();
  }

  /**
   * オブジェクトをDropした時呼ばれるイベントでサーバ通信が成功したら呼ばれるメソッド（コマ入替）
   * 左右のページの再表示を行う
   */
  private dragDropHandlerSuccess3(): void {

    // ページ更新イベント
    if (this.kymw1400Model.leftPageSelected) {
      // 選択ページが左の場合
      // 左ページのリロードボタンが活性の場合
      if (this.kymw1400Model.leftView.kymw1400KomaPageModel.reloadEnable) {
        // 左ページの更新
        this.kymw1400Model.leftView.reloadPageInfo();
      }

      // 右ページを選択
      this.kymw1400Model.leftPageSelected = false;

      // 右ページのリロードボタンが活性の場合
      if (this.kymw1400Model.rightView.kymw1400KomaPageModel.reloadEnable) {
        // 右ページの更新
        this.kymw1400Model.rightView.reloadPageInfo();
      }

      // 左ページの選択
      this.kymw1400Model.leftPageSelected = true;
    } else {
      // 選択ページが右の場合
      // 右ページのリロードボタンが活性の場合
      if (this.kymw1400Model.rightView.kymw1400KomaPageModel.reloadEnable) {
        // 右ページの更新
        this.kymw1400Model.rightView.reloadPageInfo();
      }

      // 左ページを選択
      this.kymw1400Model.leftPageSelected = true;

      // 左ページのリロードボタンが活性の場合
      if (this.kymw1400Model.leftView.kymw1400KomaPageModel.reloadEnable) {
        // 左ページの更新
        this.kymw1400Model.leftView.reloadPageInfo();
      }

      // 右ページの選択
      this.kymw1400Model.leftPageSelected = false;
    }
  }

  /**
   * 対象となるセクションのIndexを取得する
   *
   * @param {number} positionX  座標X
   * @param {number} positionY  座標Y
   * @returns {number}
   * @memberof PageComponent
   */
  getSectionByPosition(positionX: number, positionY: number): SectionComponent {

    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (positionX >= this.sectionList[i].drawSectionPositionX &&
        positionX < this.sectionList[i].drawSectionPositionX + this.sectionList[i].drawSectionSizeW &&
        positionY >= this.sectionList[i].drawSectionPositionY &&
        positionY < this.sectionList[i].drawSectionPositionY + this.sectionList[i].drawSectionSizeH) {
        return this.sectionList[i];
      }
    }
    return null;
  }

  /**
   * 入力座標がコマ内か判定しListのIndexを取得する
   *
   * @param {Array<KomaComponent>} komaList  コマリスト
   * @param {number} positionX  座標X
   * @param {number} positionY  座標Y
   * @returns {number} komaListのIndex
   * @memberof PageComponent
   */
  getIndexKomaListforCrossSet(komaList: Array<KomaComponent>, positionX: number, positionY: number): Number {
    let index = Number(-1);
    for (let i = Number(0); i < komaList.length; i++) {
      // Input座標が対象コマの描画領域内かチェック
      if ((komaList[i].drawKomaPositionX <= positionX) &&
          (positionX < komaList[i].drawKomaPositionX + komaList[i].drawKomaSizeW) &&
          (komaList[i].drawKomaPositionY <= positionY) &&
          (positionY < komaList[i].drawKomaPositionY + komaList[i].drawKomaSizeH)) {
        // クロス設定対象index取得
        index = i;
        break;
      }
    }
    return index;
  }

  canReplaceKoma(fromKoma: KomaComponent, toKoma: KomaComponent,
    fromPage: PageComponent, toPage: PageComponent, ngFunction: Function): boolean {

    // 対象ページの編集ロックがロックされている
    if (fromPage.kymw1400KomaPageModel.pageLock || toPage.kymw1400KomaPageModel.pageLock) {
      this.dialogService.openAlertGetMessage('E0513', ['編集ロックが解除されていない', '変更'], () => {
        ngFunction();
      });
      return false;
    }

    // ページ間移動で異なる生協が選択されている
    if (toPage.kymw1400KomaPageModel.otherPageFlag
      && toPage.kymw1400Model.selectedCoopCode !== toPage.kymw1400Model.unSelectedCoopCode) {
      this.dialogService.openAlertGetMessage('E0513', ['入替元コマと入替先コマの会員生協が異なる', '変更'], () => {
        ngFunction();
      });
      return false;
    }

    // 入替先コマの商品のＰコードリストと入替元コマの商品のＰコードリストが順同で完全一致しない場合
    if (fromKoma.getPcodeList().toString() !== toKoma.getPcodeList().toString()) {
      // 入替先コマが配置されているページ内の商品のＰコードに入替元コマのＰコードが含まれる場合
      if (toKoma.page.pageId !== fromKoma.page.pageId
        && this.hasPcode(fromKoma.getPcodeList())) {
        // 選択されている媒体の業態区分が'2'(デイリー)以外の場合
        if (this.kymw1400Model.selectedDutiesFormDiv !== '2') {
          // エラーメッセージを表示する
          this.dialogService.openAlertGetMessage('E0118', [], () => {
            ngFunction();
          });
          return;
        }
      }

      // 入替元コマが配置されているページ内の商品のＰコードに入替先コマのＰコードが含まれる場合
      if (toKoma.page.pageId !== fromKoma.page.pageId
        && fromKoma.page.hasPcode(toKoma.getPcodeList())) {
        // 選択されている媒体の業態区分が'2'(デイリー)以外の場合
        if (this.kymw1400Model.selectedDutiesFormDiv !== '2') {
          // エラーメッセージを表示する
          this.dialogService.openAlertGetMessage('E0118', [], () => {
            ngFunction();
          });
          return;
        }
      }
    }

    return true;

  }

  canShiftKoma(toKoma: KomaComponent, fromPage: PageComponent, toPage: PageComponent, ngFunction: Function): boolean {

    // 対象ページの編集ロックがロックされている
    if (fromPage.kymw1400KomaPageModel.pageLock || toPage.kymw1400KomaPageModel.pageLock) {
      this.dialogService.openAlertGetMessage('E0513', ['編集ロックが解除されていない', '変更'], ngFunction());
      return false;
    }

    // 異なるページでコマ詰めを行おうとしている
    if (fromPage.pageNum !== toPage.pageNum) {
      this.dialogService.openAlertGetMessage('E0513', ['詰め元コマと詰め先コマのページが異なる', '変更'], ngFunction());
      return false;
    }

    // 入替元のセクション取得
    const oldSection: SectionComponent = fromPage.getSectionByPosition(fromPage.oldKomaX, fromPage.oldKomaY);
    // 入替元の位置を取得
    const oldPosition = fromPage.getKomaProxyPosition(
      oldSection, fromPage.oldKomaX, fromPage.oldKomaY, fromPage.oldKomaW, fromPage.oldKomaH);
    // 入替元と入替先のコマサイズが異なる
    if (((oldPosition.right - oldPosition.left) / oldSection.divX).toFixed(9) !== toKoma.sizeW.toFixed(9) ||
      ((oldPosition.bottom - oldPosition.top) / oldSection.divY).toFixed(9) !== toKoma.sizeH.toFixed(9)) {
      this.dialogService.openAlertGetMessage('E0513', ['詰め元コマと詰め先コマのコマサイズが異なる', '変更'], ngFunction());
      return false;
    }

    return true;
  }

  /**
   * 実行確認
   *
   * @param {string} comaTypeName 実行処理名
   * @param {PageComponent} pageComponent 処理を実行する側のページ
   * @param {KomaComponent} fromKoma 入替元コマ
   * @param {KomaComponent} toKoma 入替先コマ
   * @param {Function} cancelFunction Noを押下時に実行する関数
   * @memberof PageComponent
   */
  public executConfirm(comaTypeName: string, pageComponent: PageComponent,
    fromKoma: KomaComponent, toKoma: KomaComponent, cancelFunction: Function) {
    this.dialogService.openAlertGetMessage('Q0152', [comaTypeName], (closeDetail) => {
      // 「はい」を押下した場合、処理実行
      if (closeDetail === PlessAlert.Yes) {
        pageComponent.replaceKoma(fromKoma, toKoma);
      } else {
        cancelFunction();
      }
    });
  }

  /**
   * 選択済みのコマをクリアする。
   */
  public clarSelectedKoma() {
    this.selectedKomaId = '';
    this.kymw1400KomaPageModel.cancelEnable = false;
  }

}
